import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SiFacebook,
  SiInstagram,
  SiLinkedin,
  SiTiktok,
  SiYoutube,
} from 'react-icons/si';
import { Platform } from '../../../types/Platforms';
import { ConnectedProfile } from '../../../types/customer';
import { getConnectedLinkedInTargets } from '../../../utils/allowedPlatformMapping';
import { Avatar, Voice, MediaSegment, TextOverlay, UGCAvatarLayout, UGCAvatarStyle, UGCVideoFormat, HeyGenEngine, HeyGenExpressiveness, UGCAspectRatio, UGCVideoResolution, VIDEO_FORMAT_TO_ASPECT_RATIO } from '../types';
import { UGCVideoEditor } from './UGCVideoEditor';
import ConfirmDialog from '../../../components/Shared/ConfirmDialog';
import { useUGCVideoAssembly } from '../hooks/useUGCVideoAssembly';
import { useMediaUpload } from '../../media/hooks/useMediaUpload';
import { uploadCustomAvatar, uploadCustomVoice } from '../../../services/contentService';
import {
  UGC_CUSTOM_IMAGES_ENABLED,
  UGC_BACKGROUND_MUSIC_PRESETS,
  UGC_DEFAULT_MUSIC_PRESET_ID,
  UGC_MUSIC_PRESET_CUSTOM,
  UGC_SCRIPT_LENGTHS,
  ugcScriptWordBudget,
  type UGCScriptLength,
} from '../constants';
import { getMediaThumbnailUrl, type MediaItem } from '../../../types/MediaItem';
import { debug } from '../../../utils/debugLogger';
import { cleanVoiceoverScript } from '../utils/cleanVoiceoverScript';
import { TEXT_FRAME_TEMPLATES } from '../utils/textFrameTemplates';
import { buildCaptionOverlays, buildCaptionOverlaysFromCues } from '../utils/buildCaptionOverlays';
import { fitScriptToWordBudget } from '../utils/fitScriptToWordBudget';
import type { CaptionCue } from '../types';
import { toBrowserPlayableMusicUrl } from '../utils/ugcMusicBrowserUrl';
import { useUGCVideoDrafts, deriveDraftTitle, MAX_DRAFTS } from '../hooks/useUGCVideoDrafts';
import { isTikTokPostingAvailable } from '../../tiktok/utils/tiktokOptions';
import { VideoDraftRecord, analyzeUGCVideoCaptions, fetchMediaByCustomer } from '../../../services/contentService';

const UPLOAD_ERROR_MESSAGE = "Oops, it looks like something went wrong. Please try again later.";

// Reel-style platforms for Target Channels (defined locally to avoid circular imports)
const REEL_PLATFORMS: Platform[] = [
  Platform.INSTAGRAM_REELS,
  Platform.YOUTUBE_SHORTS,
  Platform.TIKTOK_REELS,
  Platform.FACEBOOK_REELS,
  Platform.LINKEDIN_REELS,
];

const getPlatformLabel = (platform: Platform): string => {
  const labels: Record<string, string> = {
    [Platform.INSTAGRAM_REELS]: 'Instagram Reels',
    [Platform.YOUTUBE_SHORTS]: 'YouTube Shorts',
    [Platform.TIKTOK_REELS]: 'TikTok',
    [Platform.FACEBOOK_REELS]: 'Facebook Reels',
    [Platform.LINKEDIN_REELS]: 'LinkedIn Reels',
  };
  return labels[platform] || platform.replace(/_/g, ' ');
};

/** Brand logos for post target platform cards (Simple Icons, react-icons/si) */
const ReelPlatformIcon: React.FC<{ platform: Platform; className?: string }> = ({ platform, className }) => {
  const size = 'h-6 w-6';
  const c = className ? `${size} ${className}` : size;
  switch (platform) {
    case Platform.INSTAGRAM_REELS:
      return <SiInstagram className={c} style={{ color: '#E4405F' }} aria-hidden />;
    case Platform.YOUTUBE_SHORTS:
      return <SiYoutube className={c} style={{ color: '#FF0000' }} aria-hidden />;
    case Platform.TIKTOK_REELS:
      return <SiTiktok className={c} style={{ color: '#000000' }} aria-hidden />;
    case Platform.FACEBOOK_REELS:
      return <SiFacebook className={c} style={{ color: '#1877F2' }} aria-hidden />;
    case Platform.LINKEDIN_REELS:
      return <SiLinkedin className={c} style={{ color: '#0A66C2' }} aria-hidden />;
    default:
      return null;
  }
};

const VIDEO_FORMAT_OPTIONS: Array<{
  value: UGCVideoFormat;
  label: string;
  badge: string;
  aspectRatio: string;
  helper: string;
}> = [
  { value: 'reel', label: 'Reel', badge: '9:16', aspectRatio: '9/16', helper: 'Vertical short-form video' },
  { value: 'landscape', label: 'Landscape', badge: '16:9', aspectRatio: '16/9', helper: 'Horizontal YouTube or web video' },
  { value: 'square', label: 'Square', badge: '1:1', aspectRatio: '1/1', helper: 'Feed-friendly square video' },
];

const ENGINE_OPTIONS: Array<{ value: HeyGenEngine; label: string; badge: string; helper: string }> = [
  { value: 'avatar_iv', label: 'Avatar IV', badge: 'Standard', helper: 'Fast render, good quality for most use cases' },
  { value: 'avatar_v', label: 'Avatar V', badge: 'Premium', helper: 'More natural movement and expression — no motion prompt' },
];

const EXPRESSIVENESS_OPTIONS: Array<{ value: HeyGenExpressiveness; label: string; helper: string }> = [
  { value: 'low',    label: 'Subtle',     helper: 'Minimal movement — clean, professional' },
  { value: 'medium', label: 'Natural',    helper: 'Balanced gestures and expression' },
  { value: 'high',   label: 'Expressive', helper: 'Energetic motion — best for ads and hooks' },
];

const MOTION_PROMPT_CHAR_LIMIT = 300;

const SCRIPT_CHAR_LIMIT = 2000;
const CAPTION_CHAR_LIMIT = 2200;


// Translucent brand tint for the Video Creator badge — tied to the brand CSS var so it
// flips with dark mode (var(--color-brand-primary) is #2f4941 light / #648b7f dark).
const BRAND_TINT_BG = 'color-mix(in srgb, var(--color-brand-primary) 13%, transparent)';
// Warm Sagelyn "ribbon gold" (--color-accent-warm #c9a86c) tint — the second brand accent,
// used to give the "upload your own" path its own identity next to the green AI path.
const WARM_TINT_BG = 'color-mix(in srgb, var(--color-accent-warm) 18%, transparent)';

const WIZARD_STEPS_5 = [
  { num: 1 as const, label: 'Start' },
  { num: 2 as const, label: 'Scenes' },
  { num: 3 as const, label: 'Customize' },
  { num: 4 as const, label: 'Preview' },
  { num: 5 as const, label: 'Finalize' },
];
const WIZARD_STEPS_4 = [
  { num: 1 as const, label: 'Start' },
  { num: 2 as const, label: 'Scenes' },
  { num: 3 as const, label: 'Customize' },
  { num: 4 as const, label: 'Finalize' },
];

interface UGCVideoCreationModalProps {
  // Modal state
  isOpen: boolean;
  onClose: () => void;
  /** When 'panel', renders inline in a container (e.g. side panel); when 'modal', full-screen overlay. */
  variant?: 'modal' | 'panel';
  /** Panel only: e.g. compact Chat | Video toggle rendered in the header row. */
  panelHeaderAccessory?: React.ReactNode;

  // Data
  avatars: Avatar[];
  /** Custom avatars from backend (custom_avatars); shown in Custom Avatars tab. */
  customAvatars?: Avatar[];
  /** Called after successful custom avatar upload to refresh both avatar lists. */
  onRefreshAvatars?: () => void | Promise<void>;
  /** Reload only the avatar list (independent of voice reload). */
  onReloadAvatars?: () => void | Promise<void>;
  /** Reload only the voice list (independent of avatar reload). */
  onReloadVoices?: () => void | Promise<void>;
  voices: Voice[];
  
  // Selections
  selectedAvatar: Avatar | null;
  selectedVoice: Voice | null;
  selectedMediaIds: string[];
  /** Timeline segments (media_id + time + duration) for display and future reorder/resize. */
  timelineSegments?: MediaSegment[];
  
  // Loading states
  loadingAvatars: boolean;
  /** True once avatars have been fetched at least once; gates the "please reload" empty state. */
  avatarsFetched?: boolean;
  loadingVoices: boolean;
  
  // Selection handlers
  onSelectAvatar: (avatar: Avatar) => void;
  onSelectVoice: (voice: Voice | null) => void;
  onToggleMedia: (mediaId: string, mediaType?: 'image' | 'video', insertAtTime?: number) => void;
  /** Clear all selected scene images (timeline); keep avatar, voice, script. */
  onClearSelectedMedia?: () => void;
  /** Reorder timeline segments by index (e.g. after drag-and-drop). */
  onReorderSegments?: (fromIndex: number, toIndex: number) => void;
  /** Update a segment's properties (time/duration/fit/animations/volume/canvas layout…). */
  onUpdateSegment?: (mediaId: string, updates: Partial<MediaSegment>) => void;
  /** Remove one overlay segment from the timeline (Edit Video step). */
  onRemoveSegment?: (mediaId: string) => void;
  /** Place or move a library image on the timeline at drop time (drag from grid). */
  onPlaceMediaOnTimeline?: (mediaId: string, timeSec: number) => void;

  // Actions
  onClearSelections: () => void;
  
  // Context
  customerId: string | null;
  idToken: string;
  agentId: string;
  /** Agent tool ids from API; when provided, AI image generation requires `generate_image`. */
  agentTools?: string[];
  
  // Script generation
  onGenerateScript: (prompt: string) => Promise<void>;
  scriptContent: string;
  setScriptContent: (content: string) => void;
  streamingScriptContent: string;
  isGeneratingScript: boolean;
  
  // Error
  error?: import('../../../services/chatService').ChatStreamError | string | null;
  
  // Video generation (passed from parent to persist when modal closes)
  isGeneratingVideo?: boolean;
  generatedVideoUrl?: string | null;
  videoError?: string | null;
  generationMessage?: string | null;
  /** Backend status as returned (e.g. Queued, Audio, Video, Compositing, AVATAR_READY, FAILED; null when not generating) */
  videoStatus?: string | null;
  /** Elapsed seconds since generation started (for wait time display) */
  elapsedSeconds?: number;
  /** Base video duration in seconds from poll when AVATAR_READY; sets timeline length on custom images step. */
  baseVideoDurationSeconds?: number | null;
  /** Video ID from generate response; used for assembly (Apply images) on Step 4. */
  generatedVideoId?: string | null;
  /** Called when assembly/Creatomate polling returns the final video URL; updates preview. */
  onAssemblyCompleteVideoUrl?: (url: string) => void;
  onGenerateVideo?: (
    script: string,
    options?: {
      video_format?: UGCVideoFormat;
      avatar_layout?: UGCAvatarLayout;
      engine?: HeyGenEngine;
      expressiveness?: HeyGenExpressiveness;
      motion_prompt?: string;
      aspect_ratio?: UGCAspectRatio;
      resolution?: UGCVideoResolution;
    }
  ) => Promise<void>;
  /** Resume polling after a soft timeout ("Check again" button). */
  onRetryCheck?: () => void;
  onResetVideo?: () => void;

  // Save to platform - direct save with caption from Social Metadata section (no extra modal)
  onSaveToPlatform?: (videoUrl: string, selectedPlatforms: Platform[], script?: string, generatedCaption?: string, tags?: string[], linkedinTarget?: "personal" | "business") => void | Promise<void>;
  isSavingVideos?: boolean;
  /** For the LinkedIn Reels Personal/Business Page choice on the Finalize step. */
  connectedProfiles?: ConnectedProfile[];

  // Caption (manual entry; optional initial value from parent)
  generatedCaption?: string;
  setGeneratedCaption?: (caption: string) => void;
  /** Auto-writes the social caption from the script after the final render (like Social Posts). */
  onGenerateCaption?: (script: string) => void | Promise<void>;
  isGeneratingCaption?: boolean;

  // Draft restore: called when user picks a saved draft; parent updates its own state
  onLoadDraftSelections?: (data: {
    selectedAvatar: Avatar | null;
    selectedVoice: Voice | null;
    timelineSegments: MediaSegment[];
    scriptContent: string;
    generatedCaption: string;
    generatedVideoId: string | null;
    generatedVideoUrl: string | null;
    baseVideoDurationSeconds: number | null;
    videoStatus: string | null;
  }) => void;
}

// Avatar scale (HeyGen avatar_layout / Creatomate avatar_zoom / preview transform all
// share this one value). Default 1.0 = no zoom; the user adjusts it in Avatar layout.
const DEFAULT_AVATAR_SCALE = 1.0;
const AVATAR_SCALE_MIN = 0.5;
const AVATAR_SCALE_MAX = 2.0;
const clampAvatarScale = (n: number) =>
  Math.max(AVATAR_SCALE_MIN, Math.min(AVATAR_SCALE_MAX, n));

/**
 * CSS that mirrors the Creatomate Base-Video transform exactly, so the preview is
 * WYSIWYG with the final render. Creatomate places the base video center-anchored at
 * (50%, 50% + offsetY*100%) and sizes it to scale*100% with fit:cover; this models the
 * same with `object-fit: cover`, center anchor, and a center-origin translate+scale.
 * Use the SAME helper for the pre-gen thumbnail, the generated-video preview, and the
 * editor so all three agree. (scale & offsetY are owned by Creatomate; HeyGen renders
 * neutral — see _build_avatar_layout_overrides in the backend.)
 */
const avatarPreviewMediaStyle = (scale: number, offsetY: number): React.CSSProperties => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  transformOrigin: 'center center',
  transform:
    scale !== 1.0 || offsetY !== 0
      ? `translateY(${offsetY * 100}%) scale(${scale})`
      : undefined,
});

export const UGCVideoCreationModal: React.FC<UGCVideoCreationModalProps> = ({
  isOpen,
  onClose,
  variant = 'modal',
  panelHeaderAccessory,
  avatars,
  customAvatars: customAvatarsProp = [],
  onRefreshAvatars,
  onReloadAvatars,
  onReloadVoices,
  voices,
  selectedAvatar,
  selectedVoice,
  selectedMediaIds,
  timelineSegments = [],
  loadingAvatars,
  avatarsFetched = false,
  loadingVoices,
  onSelectAvatar,
  onSelectVoice,
  onToggleMedia,
  onClearSelectedMedia,
  onReorderSegments,
  onUpdateSegment,
  onRemoveSegment,
  onPlaceMediaOnTimeline,
  onClearSelections,
  customerId,
  idToken,
  agentId,
  agentTools,
  onGenerateScript,
  scriptContent,
  setScriptContent,
  streamingScriptContent,
  isGeneratingScript,
  error,
  // Video generation props (from parent to persist when modal closes)
  isGeneratingVideo = false,
  generatedVideoUrl = null,
  videoError = null,
  generationMessage = null,
  videoStatus = null,
  elapsedSeconds = 0,
  baseVideoDurationSeconds = null,
  generatedVideoId = null,
  onAssemblyCompleteVideoUrl,
  onGenerateVideo,
  onRetryCheck,
  onResetVideo,
  onSaveToPlatform,
  isSavingVideos = false,
  connectedProfiles = [],
  generatedCaption = '',
  setGeneratedCaption = () => {},
  onGenerateCaption,
  isGeneratingCaption = false,
  onLoadDraftSelections,
}) => {
  const navigate = useNavigate();
  const [scriptPrompt, setScriptPrompt] = useState("");
  /** Step 1 mode: let AI write the script, or type your own into a blank box. Both feed the
   *  same `localScriptContent`/`scriptContent` pipeline (scenes, captions, generation). */
  const [scriptMode, setScriptMode] = useState<'ai' | 'manual'>('ai');
  /** First thing the user sees on step 1: pick "Generate with AI" (→ the script screen) or
   *  "Upload your own" (→ device / library chooser → the editor). Skipped when the modal opens
   *  with work already in flight (restored draft, a script handed in from chat, etc.). */
  const [startChoice, setStartChoice] = useState<'ai' | 'upload' | null>(
    () => (scriptContent.trim() || generatedVideoId || generatedVideoUrl ? 'ai' : null),
  );
  /** On the "Upload your own" screen: bring a file from this device, or pick an existing video
   *  from the customer's media library. */
  const [uploadSource, setUploadSource] = useState<'device' | 'library'>('device');
  const [libraryVideos, setLibraryVideos] = useState<MediaItem[] | null>(null);
  const [libraryVideosLoading, setLibraryVideosLoading] = useState(false);
  const [libraryVideosError, setLibraryVideosError] = useState<string | null>(null);
  /** Target script length — silently appended to the prompt at submit time so the
   *  AI knows the target duration without the user having to type it. */
  const [scriptLength, setScriptLength] = useState<UGCScriptLength>('~30 sec');
  /** Word cap of the most recent "Generate Script" run — used to trim over-long AI output once streaming ends. */
  const lastScriptMaxWordsRef = useRef<number | null>(null);
  const [isLengthMenuOpen, setIsLengthMenuOpen] = useState(false);
  const lengthMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isLengthMenuOpen) return;
    const handlePointer = (e: MouseEvent) => {
      if (lengthMenuRef.current && !lengthMenuRef.current.contains(e.target as Node)) {
        setIsLengthMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsLengthMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isLengthMenuOpen]);
  const scriptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const selectedAvatarRef = useRef<HTMLButtonElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const [previewVideoPlaying, setPreviewVideoPlaying] = useState(false);
  const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
  const [videoLoadError, setVideoLoadError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
  const [linkedinTarget, setLinkedinTarget] = useState<"personal" | "business" | undefined>(undefined);
  /** Snapshot of inputs sent on the last successful assembly run; used to skip no-op re-renders. */
  const assemblyAppliedSignatureRef = useRef<string | null>(null);
  const [voiceSearchQuery, setVoiceSearchQuery] = useState("");
  const [voiceLanguageFilter, setVoiceLanguageFilter] = useState("all");
  const [voiceGenderFilter, setVoiceGenderFilter] = useState("all");
  const [voiceAccentFilter, setVoiceAccentFilter] = useState("all");
  /** Voice step tab: AI catalog voices vs the customer's cloned voices (+ upload). */
  const [voiceTab, setVoiceTab] = useState<'ai' | 'custom'>('ai');
  const [customVoiceName, setCustomVoiceName] = useState("");
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [customVoiceError, setCustomVoiceError] = useState<string | null>(null);
  const customVoiceFileInputRef = useRef<HTMLInputElement | null>(null);
  /** Voices just cloned this session — shown immediately so the user sees them before the
   *  backend's eventually-consistent voice list catches up (DynamoDB GSI propagation lag). */
  const [recentCustomVoices, setRecentCustomVoices] = useState<Voice[]>([]);

  /** Custom avatars just uploaded this session — shown immediately (local image as the
   *  thumbnail) while HeyGen processes and the eventually-consistent list catches up. */
  const [recentCustomAvatars, setRecentCustomAvatars] = useState<Avatar[]>([]);

  // Optimistic lists are per business profile — never carry them across a switch.
  useEffect(() => {
    setRecentCustomVoices([]);
    setRecentCustomAvatars([]);
  }, [customerId]);
  /** Inline list sample playback (separate from step navigation; see cleanup on unmount / leaving voice step). */
  const voiceSampleListAudioRef = useRef<HTMLAudioElement | null>(null);
  const [playingVoiceSampleId, setPlayingVoiceSampleId] = useState<string | null>(null);

  const assembly = useUGCVideoAssembly({ customerId, idToken, onFinalUrl: onAssemblyCompleteVideoUrl });
  /** Local script content for immediate feedback when typing; synced with parent scriptContent */
  const [localScriptContent, setLocalScriptContent] = useState(scriptContent);
  /** Active step: 1=Script, 2=AI Model, 3=Voice, 4=Edit Video (or Post Details when custom images off), 5=Post Details. */
  const [activeConfigStep, setActiveConfigStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  /** Preset id from `UGC_BACKGROUND_MUSIC_PRESETS` or `UGC_MUSIC_PRESET_CUSTOM`. */
  const [musicPresetId, setMusicPresetId] = useState<string>(UGC_DEFAULT_MUSIC_PRESET_ID);
  const [customMusicUrl, setCustomMusicUrl] = useState('');
  /** 0–100: browser preview gain and Creatomate background track volume. */
  const [musicMixVolumePercent, setMusicMixVolumePercent] = useState(30);
  const musicPreviewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [selectedVideoFormat, setSelectedVideoFormat] = useState<UGCVideoFormat>('reel');
  // Collapsed by default so the avatar grid is the focus of step 2; the picked format stays
  // visible in the header summary, expand to change it.
  const [videoFormatExpanded, setVideoFormatExpanded] = useState(false);
  // Quality settings are optional/advanced — collapsed by default for the same reason.
  const [qualitySettingsExpanded, setQualitySettingsExpanded] = useState(false);
  const [selectedEngine, setSelectedEngine] = useState<HeyGenEngine>('avatar_iv');
  const [expressiveness, setExpressiveness] = useState<HeyGenExpressiveness>('medium');
  const [motionPrompt, setMotionPrompt] = useState('');
  const [selectedResolution, setSelectedResolution] = useState<UGCVideoResolution>('1080p');
  const [avatarScale, setAvatarScale] = useState(DEFAULT_AVATAR_SCALE);
  const [avatarOffsetY, setAvatarOffsetY] = useState(0);

  /** avatar_ids whose thumbnail 404'd / failed to load — hide those cards entirely
   *  so the grid doesn't show empty placeholder icons. */
  const [failedThumbnailIds, setFailedThumbnailIds] = useState<Set<string>>(new Set());
  const markThumbnailFailed = useCallback((avatarId: string) => {
    setFailedThumbnailIds((prev) => {
      if (prev.has(avatarId)) return prev;
      const next = new Set(prev);
      next.add(avatarId);
      return next;
    });
  }, []);
  /** avatar_ids whose thumbnail has finished loading — until then we show a skeleton
   *  over the card so slow HeyGen CDN thumbnails don't render as blank grey boxes. */
  const [loadedThumbnailIds, setLoadedThumbnailIds] = useState<Set<string>>(new Set());
  const markThumbnailLoaded = useCallback((avatarId: string) => {
    setLoadedThumbnailIds((prev) => {
      if (prev.has(avatarId)) return prev;
      const next = new Set(prev);
      next.add(avatarId);
      return next;
    });
  }, []);

  const handlePickAvatar = (avatar: Avatar) => {
    onSelectAvatar(avatar);
  };
  // 'normal' = HeyGen's natural framing. The avatar is rendered at 16:9 (see backend
  // _compute_heygen_dimension) so it fills its frame with no bars, then Creatomate's
  // fit:cover (and this preview's object-fit:cover) crops it into the chosen format.
  // closeUp would crop again on top of that cover → over-tight, so keep normal.
  // HeyGen v3 has no avatar style or matting (SGL-497): fixed defaults keep the preview honest.
  const avatarStyle: UGCAvatarStyle = 'normal';
  const avatarMatting = false;
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captionDesign, setCaptionDesign] = useState<string>('classic');
  const [captionSpacing, setCaptionSpacing] = useState<string>('normal');
  const [captionCues, setCaptionCues] = useState<CaptionCue[] | null>(null);
  const [isAnalyzingCaptions, setIsAnalyzingCaptions] = useState(false);
  /** Shown in the Captions panel when auto-detection can't produce captions (e.g. an uploaded
   *  video whose audio couldn't be read and there's no script to fall back on) — SGL-143. */
  const [captionNotice, setCaptionNotice] = useState<string | null>(null);

  const resolvedBackgroundMusicUrl = useMemo(() => {
    if (musicPresetId === UGC_MUSIC_PRESET_CUSTOM) return customMusicUrl.trim();
    return UGC_BACKGROUND_MUSIC_PRESETS.find((p) => p.id === musicPresetId)?.url?.trim() ?? '';
  }, [musicPresetId, customMusicUrl]);

  /** Browser preview URL (can differ from assembly payload URL if needed). */
  const browserPlayableMusicUrl = useMemo(
    () => (resolvedBackgroundMusicUrl ? toBrowserPlayableMusicUrl(resolvedBackgroundMusicUrl) : ''),
    [resolvedBackgroundMusicUrl]
  );

  useEffect(() => {
    if (activeConfigStep !== 4) {
      musicPreviewAudioRef.current?.pause();
    }
  }, [activeConfigStep]);


  useEffect(() => {
    const el = musicPreviewAudioRef.current;
    if (!el) return;
    el.volume = Math.min(1, Math.max(0, musicMixVolumePercent / 100));
  }, [musicMixVolumePercent, browserPlayableMusicUrl]);

  useEffect(() => {
    return () => {
      const el = voiceSampleListAudioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
    };
  }, []);

  useEffect(() => {
    if (activeConfigStep !== 3) {
      const el = voiceSampleListAudioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
      setPlayingVoiceSampleId(null);
    }
  }, [activeConfigStep]);

  /** AI Model step source filter (public avatars vs custom avatars) */
  const [avatarSource, setAvatarSource] = useState<'public' | 'custom'>('public');
  /** Custom Avatars tab: selected image file for "Generate custom avatar" (preview URL for display) */
  const [selectedCustomAvatarImage, setSelectedCustomAvatarImage] = useState<File | null>(null);
  const [selectedCustomAvatarPreviewUrl, setSelectedCustomAvatarPreviewUrl] = useState<string | null>(null);
  /** Custom avatars list (source TBD — will be wired when API is known) */
  const [isUploadingCustomAvatar, setIsUploadingCustomAvatar] = useState(false);
  const [customAvatarError, setCustomAvatarError] = useState<string | null>(null);
  const [customAvatarSuccessMessage, setCustomAvatarSuccessMessage] = useState<string | null>(null);
  const customAvatarFileInputRef = useRef<HTMLInputElement>(null);
  /** Social Metadata: tags for captions */
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  /** Text overlays shown in the timeline TEXT track and rendered on the preview at their respective times. */
  const [textOverlays, setTextOverlays] = useState<TextOverlay[]>([]);
  /** Temporary: local video blob URL loaded for editor testing (no HeyGen credit needed). */
  const testVideoFileInputRef = useRef<HTMLInputElement>(null);
  const [testVideoObjectUrl, setTestVideoObjectUrl] = useState<string | null>(null);
  /** Duration read from the test video file; overrides the prop when set. */
  const [localBaseVideoDuration, setLocalBaseVideoDuration] = useState<number | null>(null);
  /** When user loads a local video as base, we upload it to S3 and synthesise a video_id
   * so the assembly endpoint can render it through Creatomate the same as a real HeyGen video. */
  const [localTestVideoId, setLocalTestVideoId] = useState<string | null>(null);
  const [localTestVideoUrl, setLocalTestVideoUrl] = useState<string | null>(null);
  const [isUploadingTestVideo, setIsUploadingTestVideo] = useState(false);
  const [testVideoUploadError, setTestVideoUploadError] = useState<string | null>(null);
  const { upload: uploadTestVideo } = useMediaUpload({
    onUploadComplete: (cloudFrontUrl) => {
      const newId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? `test-${crypto.randomUUID()}`
        : `test-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      setLocalTestVideoId(newId);
      setLocalTestVideoUrl(cloudFrontUrl);
      onAssemblyCompleteVideoUrl?.(cloudFrontUrl);
      setIsUploadingTestVideo(false);
      setActiveConfigStep(4);
    },
    onError: (msg) => {
      setIsUploadingTestVideo(false);
      setTestVideoUploadError(msg || 'Failed to upload test video');
    },
  });
  // ── Draft save / restore ──────────────────────────────────────────────────
  const { drafts, isLoading: isDraftsLoading, saveDraft, deleteDraft } = useUGCVideoDrafts({ customerId, idToken });
  const [isDraftMenuOpen, setIsDraftMenuOpen] = useState(false);
  const [showAllDrafts, setShowAllDrafts] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftToast, setDraftToast] = useState<{ message: string; isError?: boolean } | null>(null);
  /** When true, the "save as draft before closing?" dialog is shown (triggered by the X button). */
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showBusyCloseConfirm, setShowBusyCloseConfirm] = useState(false);
  const draftMenuRef = useRef<HTMLDivElement>(null);
  const draftToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isDraftMenuOpen) { setShowAllDrafts(false); return; }
    const handlePointer = (e: MouseEvent) => {
      if (draftMenuRef.current && !draftMenuRef.current.contains(e.target as Node)) setIsDraftMenuOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsDraftMenuOpen(false); };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handlePointer); document.removeEventListener('keydown', handleKey); };
  }, [isDraftMenuOpen]);

  const showDraftToast = useCallback((message: string, isError = false) => {
    setDraftToast({ message, isError });
    if (draftToastTimerRef.current) clearTimeout(draftToastTimerRef.current);
    draftToastTimerRef.current = setTimeout(() => setDraftToast(null), 3000);
  }, []);

  const handleSaveDraft = useCallback(async (): Promise<boolean> => {
    if (drafts.length >= MAX_DRAFTS) {
      showDraftToast('Draft limit reached (20). Delete a draft to save a new one.', true);
      return false;
    }
    setIsSavingDraft(true);
    setIsDraftMenuOpen(false);
    try {
      await saveDraft({
        title: deriveDraftTitle(localScriptContent),
        scriptContent: localScriptContent,
        activeConfigStep,
        selectedAvatarId: selectedAvatar?.avatar_id ?? null,
        selectedAvatarType: selectedAvatar?.avatar_type ?? null,
        selectedVoiceId: selectedVoice?.voice_id ?? null,
        selectedVideoFormat,
        selectedEngine,
        expressiveness,
        motionPrompt,
        selectedResolution,
        avatarScale,
        avatarOffsetY,
        avatarStyle,
        avatarMatting,
        // Generated video — so reloading the draft restores the actual video, not just settings.
        generatedVideoId,
        generatedVideoUrl,
        baseVideoDurationSeconds,
        videoStatus,
        timelineSegments,
        textOverlays,
        musicPresetId,
        customMusicUrl,
        musicMixVolumePercent,
        captionsEnabled,
        captionDesign,
        captionSpacing,
        selectedPlatforms: selectedPlatforms.map(String),
        generatedCaption,
        tags,
      });
      showDraftToast('Draft saved');
      return true;
    } catch {
      showDraftToast('Failed to save draft', true);
      return false;
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    drafts.length, saveDraft, localScriptContent, activeConfigStep,
    selectedAvatar, selectedVoice, selectedVideoFormat, selectedEngine,
    expressiveness, motionPrompt, selectedResolution, avatarScale, avatarOffsetY,
    avatarStyle, avatarMatting, timelineSegments, textOverlays,
    musicPresetId, customMusicUrl, musicMixVolumePercent, captionsEnabled,
    captionDesign, captionSpacing, selectedPlatforms, generatedCaption, tags, showDraftToast,
    generatedVideoId, generatedVideoUrl, baseVideoDurationSeconds, videoStatus,
  ]);

  /** "Save as draft" chosen on the close dialog: persist, then close once the toast is visible. */
  const handleSaveDraftAndClose = useCallback(async () => {
    const ok = await handleSaveDraft();
    if (ok) {
      // Keep the editor mounted briefly so the "Draft saved" toast is visible, then close.
      setShowCloseDialog(false);
      setTimeout(() => onClose(), 1100);
    } else {
      // Save failed (e.g. draft limit) — keep the editor open so nothing is lost.
      setShowCloseDialog(false);
    }
  }, [handleSaveDraft, onClose]);

  /** "Don't save" chosen on the close dialog: discard and close immediately. */
  const handleDiscardAndClose = useCallback(() => {
    setShowCloseDialog(false);
    onClose();
  }, [onClose]);

  const handleLoadDraft = useCallback((record: VideoDraftRecord) => {
    setIsDraftMenuOpen(false);
    const d = record.draft_data;

    const allAvatars = [...avatars, ...(customAvatarsProp ?? [])];
    const resolvedAvatar = d.selectedAvatarId
      ? (allAvatars.find((a) => a.avatar_id === d.selectedAvatarId) ?? null)
      : null;
    const resolvedVoice = d.selectedVoiceId
      ? (voices.find((v) => v.voice_id === d.selectedVoiceId) ?? null)
      : null;

    onLoadDraftSelections?.({
      selectedAvatar: resolvedAvatar,
      selectedVoice: resolvedVoice,
      timelineSegments: d.timelineSegments ?? [],
      scriptContent: d.scriptContent ?? '',
      generatedCaption: d.generatedCaption ?? '',
      generatedVideoId: d.generatedVideoId ?? null,
      generatedVideoUrl: d.generatedVideoUrl ?? null,
      baseVideoDurationSeconds: d.baseVideoDurationSeconds ?? null,
      videoStatus: d.videoStatus ?? null,
    });

    setLocalScriptContent(d.scriptContent ?? '');
    setScriptContent(d.scriptContent ?? '');
    setStartChoice('ai'); // a restored draft skips the "how do you want to start" picker
    setActiveConfigStep(d.activeConfigStep ?? 1);
    setTextOverlays(d.textOverlays ?? []);
    setMusicPresetId(d.musicPresetId ?? UGC_DEFAULT_MUSIC_PRESET_ID);
    setCustomMusicUrl(d.customMusicUrl ?? '');
    setMusicMixVolumePercent(d.musicMixVolumePercent ?? 30);
    setSelectedVideoFormat(d.selectedVideoFormat ?? 'reel');
    setSelectedEngine(d.selectedEngine ?? 'avatar_iv');
    setExpressiveness(d.expressiveness ?? 'medium');
    setMotionPrompt(d.motionPrompt ?? '');
    // 4K isn't available on Avatar IV/V since the HeyGen v3 move (SGL-497); older drafts fall back to 1080p.
    setSelectedResolution(d.selectedResolution && d.selectedResolution !== '4k' ? d.selectedResolution : '1080p');
    // Older drafts stored avatarZoom (Creatomate-only). It carries the same intent as
    // avatarScale (now unified), so fall back to it when avatarScale is missing.
    setAvatarScale(d.avatarScale ?? d.avatarZoom ?? DEFAULT_AVATAR_SCALE);
    setAvatarOffsetY(d.avatarOffsetY ?? 0);
    setCaptionsEnabled(d.captionsEnabled ?? false);
    setCaptionDesign(d.captionDesign ?? 'classic');
    setCaptionSpacing(d.captionSpacing ?? 'normal');
    setSelectedPlatforms((d.selectedPlatforms ?? []) as Platform[]);
    setTags(d.tags ?? []);

    const missingAvatar = d.selectedAvatarId && !resolvedAvatar;
    showDraftToast(
      missingAvatar
        ? `Draft restored — avatar no longer available, please re-select.`
        : `Draft restored — ${record.title}`
    );
  }, [
    avatars, customAvatarsProp, voices, onLoadDraftSelections,
    setScriptContent, showDraftToast,
  ]);

  const addTextOverlay = (timeSec: number) => {
    const id = `text-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const newDuration = 3;
    const newEnd = timeSec + newDuration;
    setTextOverlays((prev) => {
      // Find overlays already visible during the new overlay's time window
      const overlapping = prev.filter(o => o.time < newEnd && (o.time + o.duration) > timeSec);
      // Stagger y so each new overlay lands at a distinct position (avoids stacking)
      const yPresets = [80, 20, 50, 35, 65];
      const takenYs = overlapping.map(o =>
        o.y_pct ?? (o.position === 'top' ? 12 : o.position === 'center' ? 50 : 80),
      );
      const chosenY =
        yPresets.find(y => !takenYs.some(ty => Math.abs(ty - y) < 15)) ??
        yPresets[overlapping.length % yPresets.length];
      const position: 'top' | 'center' | 'bottom' =
        chosenY < 33 ? 'top' : chosenY < 66 ? 'center' : 'bottom';
      return [...prev, {
        id,
        text: 'Text here',
        time: timeSec,
        duration: newDuration,
        style: 'subtitle' as const,
        position,
        x_pct: 50,
        y_pct: chosenY,
      }];
    });
  };

  const updateTextOverlay = (id: string, updates: Partial<TextOverlay>) => {
    setTextOverlays((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t)));
  };

  const removeTextOverlay = (id: string) => {
    setTextOverlays((prev) => prev.filter((t) => t.id !== id));
  };

  /** Insert a ready-made text-frame template's overlay(s) at the given playhead time. */
  const applyTextTemplate = (templateId: string, atTime: number) => {
    const tpl = TEXT_FRAME_TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    const overlays = tpl.build(atTime);
    if (overlays.length) setTextOverlays((prev) => [...prev, ...overlays]);
  };

  const handleTestVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!customerId || !idToken) {
      setTestVideoUploadError('Sign in required to upload a test video.');
      return;
    }
    if (testVideoObjectUrl) URL.revokeObjectURL(testVideoObjectUrl);
    const blobUrl = URL.createObjectURL(file);
    setTestVideoObjectUrl(blobUrl);

    // Read duration locally before kicking off the upload.
    const vid = document.createElement('video');
    vid.preload = 'metadata';
    vid.onloadedmetadata = () => {
      setLocalBaseVideoDuration(isFinite(vid.duration) && vid.duration > 0 ? vid.duration : null);
      vid.src = '';
    };
    vid.onerror = () => { setLocalBaseVideoDuration(null); vid.src = ''; };
    vid.src = blobUrl;

    setTestVideoUploadError(null);
    setStartChoice('upload');
    setIsUploadingTestVideo(true);
    // Upload to S3 so Creatomate can fetch it; onUploadComplete sets state + advances step.
    uploadTestVideo({ file, customerId, idToken, mediaType: 'processed' });
  };

  /** Use an existing library video as the base — no upload, it already lives on our CloudFront. */
  const handleLibraryVideoPick = (item: MediaItem) => {
    const url = (item.cloudfront_url || getMediaThumbnailUrl(item) || '').trim();
    if (!url) {
      setTestVideoUploadError('That video is missing its URL — try another.');
      return;
    }
    const newId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? `test-${crypto.randomUUID()}`
      : `test-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    // Pull the real duration client-side (library metadata doesn't carry it).
    setLocalBaseVideoDuration(null);
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.onloadedmetadata = () => {
      setLocalBaseVideoDuration(isFinite(probe.duration) && probe.duration > 0 ? probe.duration : null);
      probe.src = '';
    };
    probe.onerror = () => { probe.src = ''; };
    probe.src = url;

    setTestVideoUploadError(null);
    setLocalTestVideoId(newId);
    setLocalTestVideoUrl(url);
    onAssemblyCompleteVideoUrl?.(url);
    setStartChoice('upload');
    setActiveConfigStep(4);
  };

  const handleApplyCustomImages = async () => {
    // Pick the right video_id: a real HeyGen video, or the synthetic id for a locally-uploaded test video.
    const effectiveVideoId = generatedVideoId || localTestVideoId;
    if (!effectiveVideoId) {
      // No base video at all — nothing for Creatomate to work with.
      setActiveConfigStep(5);
      return;
    }
    const usingLocalTestVideo = !generatedVideoId && Boolean(localTestVideoId && localTestVideoUrl);
    try {
      await assembly.assembleVideo(
        effectiveVideoId,
        timelineSegments,
        resolvedBackgroundMusicUrl,
        musicMixVolumePercent,
        {
          // When script-derived caption overlays exist they ARE the captions (rendered via
          // text_overlays below), so suppress the backend's STT auto-caption to avoid doubles.
          captions_enabled: captionsEnabled && !textOverlays.some((t) => t.source === 'caption'),
          caption_design: captionDesign,
          caption_spacing: captionSpacing,
          // Avatar framing is owned entirely by Creatomate: avatar_zoom scales the
          // Base-Video, avatar_offset_y shifts it vertically. HeyGen renders neutral,
          // so the preview (CSS mirror of this transform) is WYSIWYG with the render.
          avatar_zoom: avatarScale,
          avatar_offset_y: avatarOffsetY,
          ...(textOverlays.length > 0 ? {
            text_overlays: textOverlays.map((t) => ({
              text: t.text,
              time: t.time,
              duration: t.duration,
              style: t.style,
              position: t.position,
              ...(t.x_pct !== undefined ? { x_pct: t.x_pct } : {}),
              ...(t.y_pct !== undefined ? { y_pct: t.y_pct } : {}),
              animation: t.animation,
              ...(t.anim_duration !== undefined ? { anim_duration: t.anim_duration } : {}),
              ...(t.animation_out !== undefined ? { animation_out: t.animation_out } : {}),
              ...(t.anim_out_duration !== undefined ? { anim_out_duration: t.anim_out_duration } : {}),
              design: t.design,
              ...(t.fontSize !== undefined ? { font_size: t.fontSize } : {}),
              ...(t.rotation !== undefined && t.rotation !== 0 ? { rotation: t.rotation } : {}),
              ...(t.color !== undefined ? { color: t.color } : {}),
              ...(t.bold ? { bold: true } : {}),
              ...(t.italic ? { italic: true } : {}),
              ...(t.outline ? { outline: true } : {}),
              ...(t.outline && t.outlineColor ? { outline_color: t.outlineColor } : {}),
              ...(t.fontFamily ? { font_family: t.fontFamily } : {}),
              ...(t.width !== undefined ? { width: t.width } : {}),
              // Always send the key: '' = "no background". The editor never shows a
              // design-default background, so the render must honour exactly this.
              background_color: t.backgroundColor ?? '',
            })),
          } : {}),
          // When the base is a locally-uploaded test video, tell the backend the avatar
          // URL + format so it can synthesise a status record for this synthetic video_id.
          ...(usingLocalTestVideo && localTestVideoUrl ? {
            avatar_video_url: localTestVideoUrl,
            video_format: selectedVideoFormat,
          } : {}),
        }
      );
      assemblyAppliedSignatureRef.current = currentAssemblySignature;
      setActiveConfigStep(5);
    } catch {
      // assembly.assemblyError is shown in the header error banner
    }
  };

  /** media_id -> thumbnail URL for timeline segment blocks (from Edit Video step on load/refetch) */
  const [segmentThumbnailsMap, setSegmentThumbnailsMap] = useState<Record<string, string>>({});
  /** media_id -> direct video URL for Pexels video preview in timeline edit panel */
  const [segmentVideoUrlsMap, setSegmentVideoUrlsMap] = useState<Record<string, string>>({});

  /** Populate the thumbnail / video-URL maps from a list of media items. Shared by the
   *  Media step's onMediaLoaded and the draft-load prefetch so timeline overlays resolve
   *  the same way regardless of how the media list arrived. */
  const ingestMediaItems = useCallback((list: MediaItem[]) => {
    const VIDEO_EXT = /\.(mp4|webm|mov|m4v|avi|mkv)(\?|#|$)/i;
    // Only store image URLs as thumbnails — never video file URLs (MP4 etc.)
    setSegmentThumbnailsMap((prev) => ({
      ...prev,
      ...Object.fromEntries(
        list
          .map((m) => [m.media_id, getMediaThumbnailUrl(m)] as const)
          .filter(([, url]) => Boolean(url) && !VIDEO_EXT.test(url))
      ),
    }));
    const videoUrls = list
      .filter((m) => (m.media_type || '').trim().toLowerCase() === 'video')
      .map((m) => [m.media_id, getMediaThumbnailUrl(m)] as const)
      .filter(([, url]) => Boolean(url) && VIDEO_EXT.test(url));
    if (videoUrls.length > 0) {
      setSegmentVideoUrlsMap((prev) => ({ ...prev, ...Object.fromEntries(videoUrls) }));
    }
  }, []);

  // Resolve timeline-segment media as soon as segments exist (e.g. restored from a draft),
  // without waiting for the user to open the Media step. Without this, overlays render blank
  // until the Media tab's own fetch fires. Re-runs only when a segment's media_id isn't yet
  // in either map; a ref dedupes so we don't refetch the same ids repeatedly.
  const prefetchedMediaIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!customerId || !idToken) return;
    const missing = timelineSegments
      .map((s) => s.media_id)
      .filter(
        (id) =>
          id &&
          !segmentThumbnailsMap[id] &&
          !segmentVideoUrlsMap[id] &&
          !prefetchedMediaIdsRef.current.has(id)
      );
    if (missing.length === 0) return;
    missing.forEach((id) => prefetchedMediaIdsRef.current.add(id));
    let cancelled = false;
    (async () => {
      try {
        const { medias } = await fetchMediaByCustomer(customerId, idToken, { limit: 50 });
        if (cancelled) return;
        ingestMediaItems(medias as MediaItem[]);
      } catch (err) {
        debug.error('Failed to prefetch timeline media:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [timelineSegments, customerId, idToken, segmentThumbnailsMap, segmentVideoUrlsMap, ingestMediaItems]);
  const loadLibraryVideos = useCallback(async () => {
    if (!customerId || !idToken) return;
    setLibraryVideosLoading(true);
    setLibraryVideosError(null);
    try {
      const { medias } = await fetchMediaByCustomer(customerId, idToken, { limit: 50, mediaType: 'video' });
      const vids = (medias as MediaItem[]).filter((m) => {
        const t = (m.media_type || '').toLowerCase();
        return t !== 'audio' && t !== 'image' && t !== 'photo';
      });
      setLibraryVideos(vids);
    } catch (err: any) {
      debug.error('Failed to load library videos:', err);
      setLibraryVideosError(err?.message || 'Failed to load your videos');
    } finally {
      setLibraryVideosLoading(false);
    }
  }, [customerId, idToken]);

  // Load the customer's library videos the first time the "Your library" tab is opened.
  // (Guards, not a `cancelled` flag — an in-effect setState must not re-run into a cleanup
  // that aborts its own fetch, which is what left the tab spinning forever.)
  useEffect(() => {
    if (
      startChoice === 'upload' &&
      uploadSource === 'library' &&
      libraryVideos === null &&
      !libraryVideosLoading &&
      !libraryVideosError
    ) {
      void loadLibraryVideos();
    }
  }, [startChoice, uploadSource, libraryVideos, libraryVideosLoading, libraryVideosError, loadLibraryVideos]);

  /** Custom thumbnail URLs for Post Details step (uploaded images; upload adds to list) */
  const [customThumbnailUrls, setCustomThumbnailUrls] = useState<string[]>([]);
  const [thumbnailUploadError, setThumbnailUploadError] = useState(false);
  /** Which thumbnail is selected: 'video' or one of the custom uploaded images */
  const [selectedThumbnailSource, setSelectedThumbnailSource] = useState<'video' | 'custom'>('video');
  /** Index into customThumbnailUrls when selectedThumbnailSource === 'custom' */
  const [selectedCustomThumbnailIndex, setSelectedCustomThumbnailIndex] = useState(0);
  const thumbnailFileInputRef = useRef<HTMLInputElement>(null);

  const { upload: uploadThumbnail, isUploading: isUploadingThumbnail, resetUpload: resetThumbnailUpload } = useMediaUpload({
    onUploadComplete: (cloudFrontUrl) => {
      setCustomThumbnailUrls((prev) => [...prev, cloudFrontUrl]);
      setSelectedThumbnailSource('custom');
      setSelectedCustomThumbnailIndex(customThumbnailUrls.length);
      resetThumbnailUpload();
    },
    onError: () => setThumbnailUploadError(true),
  });

  const togglePlatform = (platform: Platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const handleThumbnailUploadClick = () => {
    setThumbnailUploadError(false);
    resetThumbnailUpload();
    thumbnailFileInputRef.current?.click();
  };

  const handleThumbnailFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!customerId || !idToken) {
      setThumbnailUploadError(true);
      return;
    }
    await uploadThumbnail({ file, customerId, idToken, mediaType: 'processed' });
  };

  const handleCustomAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (selectedCustomAvatarPreviewUrl) URL.revokeObjectURL(selectedCustomAvatarPreviewUrl);
    setSelectedCustomAvatarPreviewUrl(null);
    setSelectedCustomAvatarImage(null);
    setCustomAvatarError(null);
    setCustomAvatarSuccessMessage(null);
    if (!file || !file.type.startsWith('image/')) return;
    setSelectedCustomAvatarImage(file);
    setSelectedCustomAvatarPreviewUrl(URL.createObjectURL(file));
  };

  const clearCustomAvatarSelection = () => {
    if (selectedCustomAvatarPreviewUrl) URL.revokeObjectURL(selectedCustomAvatarPreviewUrl);
    setSelectedCustomAvatarPreviewUrl(null);
    setSelectedCustomAvatarImage(null);
    customAvatarFileInputRef.current?.value && (customAvatarFileInputRef.current.value = '');
  };

  const handleGenerateCustomAvatar = async () => {
    if (!selectedCustomAvatarImage || !customerId || !idToken) return;
    setCustomAvatarError(null);
    setIsUploadingCustomAvatar(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const raw = result?.includes(',') ? result.split(',')[1] : result;
          if (raw) resolve(raw);
          else reject(new Error('Failed to read image'));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(selectedCustomAvatarImage);
      });
      const contentType = selectedCustomAvatarImage.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const result = (await uploadCustomAvatar(customerId, base64, idToken, contentType)) as
        { talking_photo_id?: string; name?: string } | undefined;
      // Show the new avatar immediately with the local image as its thumbnail — the backend
      // list is eventually consistent, so without this it only appears after a later reload.
      if (result?.talking_photo_id) {
        const optimisticThumb = URL.createObjectURL(selectedCustomAvatarImage);
        setRecentCustomAvatars((prev) => [
          {
            avatar_id: result.talking_photo_id!,
            name: result.name || 'Custom Avatar',
            thumbnail_url: optimisticThumb,
            provider: 'HEYGEN',
            avatar_type: 'talking_photo' as const,
          },
          ...prev.filter((a) => a.avatar_id !== result.talking_photo_id),
        ]);
      }
      clearCustomAvatarSelection();
      onRefreshAvatars?.();
      setCustomAvatarError(null);
      setCustomAvatarSuccessMessage('Your avatar is being created. Please allow a few moments for it to appear.');
    } catch {
      setCustomAvatarSuccessMessage(null);
      setCustomAvatarError('Ooops! We had issues creating your avatar. Please try again.');
    } finally {
      setIsUploadingCustomAvatar(false);
    }
  };

  const handleDownloadVideo = async () => {
    if (!generatedVideoUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      const res = await fetch(generatedVideoUrl, { mode: 'cors' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `video-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(generatedVideoUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  /** The finalised AI script (post length-trim) last written to the parent — lets the
   *  local-sync effect below recognise AI output even after it was trimmed. */
  const finalStreamedScriptRef = useRef<string>('');

  // Update script content when streaming completes — trimmed to the requested length's
  // word budget if the model badly overshot (backstop for the prompt-level budget).
  useEffect(() => {
    if (streamingScriptContent && !isGeneratingScript && streamingScriptContent.trim()) {
      const maxWords = lastScriptMaxWordsRef.current;
      const finalScript = maxWords
        ? fitScriptToWordBudget(streamingScriptContent, maxWords)
        : streamingScriptContent;
      if (finalScript !== streamingScriptContent) {
        debug.log(
          `[ugc] generated script trimmed to fit ~${maxWords} words ` +
            `(was ${(streamingScriptContent.match(/\S+/g) || []).length})`,
        );
      }
      finalStreamedScriptRef.current = finalScript;
      setScriptContent(finalScript);
    }
  }, [streamingScriptContent, isGeneratingScript, setScriptContent]);

  // Sync local script from parent only when AI streaming completes (avoids overwriting user typing)
  useEffect(() => {
    if (streamingScriptContent && !isGeneratingScript && scriptContent === finalStreamedScriptRef.current) {
      setLocalScriptContent(scriptContent);
    }
  }, [scriptContent, streamingScriptContent, isGeneratingScript]);

  /** When parent has a script but the textarea is still empty (e.g. AI wrote to parent before sync ran), mirror into local so the wizard and Process Video see the same transcript. */
  useEffect(() => {
    if (isGeneratingScript) return;
    if (!scriptContent.trim()) return;
    if (localScriptContent.trim().length > 0) return;
    setLocalScriptContent(scriptContent);
  }, [scriptContent, localScriptContent, isGeneratingScript]);

  // Revoke custom avatar preview object URL when cleared or changed to avoid leaks
  useEffect(() => {
    return () => {
      if (selectedCustomAvatarPreviewUrl) URL.revokeObjectURL(selectedCustomAvatarPreviewUrl);
    };
  }, [selectedCustomAvatarPreviewUrl]);

  // Revoke test video blob URL on unmount
  useEffect(() => {
    return () => {
      if (testVideoObjectUrl) URL.revokeObjectURL(testVideoObjectUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset preview playing state, current time, and load error when video URL changes
  useEffect(() => {
    if (!generatedVideoUrl) {
      setPreviewVideoPlaying(false);
      setPreviewCurrentTime(0);
      setVideoLoadError(null);
    } else {
      setVideoLoadError(null);
      setPreviewCurrentTime(0);
    }
  }, [generatedVideoUrl]);

  /**
   * After a fresh final render, auto-write the social post caption from the script — the same way
   * the Social Posts flow generates one — so the Post Details caption box fills itself. Runs once
   * per video URL and never overwrites a caption the user typed or one restored from a draft.
   */
  const autoCaptionedUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onGenerateCaption || !generatedVideoUrl) return;
    if (autoCaptionedUrlRef.current === generatedVideoUrl || isGeneratingCaption) return;
    // Adopt this URL without firing when a caption already exists (manual edit / restored draft).
    if (generatedCaption.trim()) {
      autoCaptionedUrlRef.current = generatedVideoUrl;
      return;
    }
    const script = localScriptContent.trim() || scriptContent.trim();
    if (!script) return;
    autoCaptionedUrlRef.current = generatedVideoUrl;
    void onGenerateCaption(script);
  }, [
    generatedVideoUrl,
    generatedCaption,
    isGeneratingCaption,
    onGenerateCaption,
    localScriptContent,
    scriptContent,
  ]);

  // Always run Creatomate: HeyGen renders the avatar at 16:9 (see backend
  // _compute_heygen_dimension), so the raw avatar clip is NOT the final format. As soon as
  // it's ready, kick off a Creatomate pass (with whatever overlays/zoom/offset are set —
  // usually none yet) so `generatedVideoUrl` becomes the format-correct deliverable, even
  // if the user never opens the editor or saves with no edits. Runs once per generated
  // video; later manual edits re-assemble via handleApplyCustomImages. On failure we reset
  // the guard so a retry (manual apply / re-generate) can still produce the cropped final.
  // NOTE: must stay above the `if (!isOpen) return null` early return (rules-of-hooks).
  const autoAssembledVideoIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (videoStatus !== 'AVATAR_READY' || !generatedVideoId) return;
    if (assembly.isAssembling) return;
    if (autoAssembledVideoIdRef.current === generatedVideoId) return;
    autoAssembledVideoIdRef.current = generatedVideoId;
    void assembly
      .assembleVideo(
        generatedVideoId,
        timelineSegments,
        resolvedBackgroundMusicUrl,
        musicMixVolumePercent,
        {
          captions_enabled: captionsEnabled && !textOverlays.some((t) => t.source === 'caption'),
          caption_design: captionDesign,
          caption_spacing: captionSpacing,
          avatar_zoom: avatarScale,
          avatar_offset_y: avatarOffsetY,
        }
      )
      .catch(() => {
        autoAssembledVideoIdRef.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoStatus, generatedVideoId, assembly.isAssembling]);

  if (!isOpen) return null;

  /** Transcript used for limits, readiness, and generate — parent `scriptContent` can hold AI output while `localScriptContent` is still empty until sync. */
  const transcriptForVideo = localScriptContent.trim() || scriptContent.trim();
  const hasScript = transcriptForVideo.length > 0;
  const isOverLimit = transcriptForVideo.length > SCRIPT_CHAR_LIMIT;
  const overBy = Math.max(0, transcriptForVideo.length - SCRIPT_CHAR_LIMIT);
  const isReady = !!(selectedAvatar && selectedVoice && hasScript);
  const selectedVideoFormatOption =
    VIDEO_FORMAT_OPTIONS.find((option) => option.value === selectedVideoFormat) ?? VIDEO_FORMAT_OPTIONS[0];
  const selectedEngineOption = ENGINE_OPTIONS.find((option) => option.value === selectedEngine);
  const isTalkingPhotoAvatar = selectedAvatar?.avatar_type === 'talking_photo';
  const hasAssemblyChanges =
    timelineSegments.length > 0 || resolvedBackgroundMusicUrl.length > 0 || captionsEnabled || textOverlays.length > 0 || avatarScale !== 1.0;

  // ── Editable captions from the verbatim script ──
  // Captions are built from the exact words the avatar speaks (the script), not from
  // backend speech-to-text — so they're error-free to begin with and fully editable on the
  // timeline. They render through the existing text_overlays path; the backend STT auto-caption
  // is suppressed whenever these exist (see assembly call) to avoid double captions.
  const captionDuration = (localBaseVideoDuration ?? baseVideoDurationSeconds) ?? 60;
  const hasCaptionOverlays = textOverlays.some((t) => t.source === 'caption');

  // NOTE: declared after the `if (!isOpen) return null` early return above, so these are plain
  // functions (not hooks) — matching addTextOverlay/updateTextOverlay nearby.
  /** Rebuild script-derived caption overlays, preserving any manually added overlays.
   *  Uses real word timestamps (ElevenLabs cues) when available; falls back to estimate. */
  const populateCaptionsFromScript = () => {
    const design = (captionDesign as TextOverlay['design']) || 'classic';
    const captions = captionCues && captionCues.length > 0
      ? buildCaptionOverlaysFromCues(captionCues, { design })
      : buildCaptionOverlays(transcriptForVideo, captionDuration, { design });
    setTextOverlays((prev) => [...prev.filter((t) => t.source !== 'caption'), ...captions]);
  };

  /** Captions toggle: runs Whisper STT on the video's audio, shows a spinner, then populates
   *  real-timing caption overlays. Falls back to a script estimate when a script exists; for a
   *  user-uploaded base video (no script) STT is the only source, so a failure surfaces a note
   *  rather than silently doing nothing (SGL-143). */
  const handleCaptionsEnabledChange = async (enabled: boolean) => {
    setCaptionsEnabled(enabled);
    setCaptionNotice(null);
    if (!enabled) {
      setTextOverlays((prev) => prev.filter((t) => t.source !== 'caption'));
      return;
    }
    if (hasCaptionOverlays) return;

    const hasScript = transcriptForVideo.length > 0;
    const usingUploadedBase = !generatedVideoId && Boolean(localTestVideoId && localTestVideoUrl);
    const effectiveVideoId = generatedVideoId || localTestVideoId;

    // SGL-143: an uploaded base video has no script — once STT returns word cues, surface the
    // detected transcript in the Script panel (and the step-1 editor) so it can be seen/edited.
    const surfaceTranscript = (cues: CaptionCue[]) => {
      if (!usingUploadedBase || hasScript || cues.length === 0) return;
      const transcript = cues.map((c) => c.word).join(' ').trim();
      if (transcript) {
        setLocalScriptContent(transcript);
        setScriptContent(transcript);
      }
    };

    // Reuse cues from a previous analyze call in this session
    if (captionCues && captionCues.length > 0) {
      surfaceTranscript(captionCues);
      populateCaptionsFromScript();
      return;
    }
    if (!effectiveVideoId || !customerId || !idToken) {
      if (hasScript) populateCaptionsFromScript();
      else setCaptionNotice('Turn on captions after the video is ready so we can read its audio.');
      return;
    }
    setIsAnalyzingCaptions(true);
    try {
      const result = await analyzeUGCVideoCaptions(
        effectiveVideoId,
        customerId,
        idToken,
        usingUploadedBase ? (localTestVideoUrl ?? undefined) : undefined,
      );
      setCaptionCues(result.cues);
      surfaceTranscript(result.cues);
      const design = (captionDesign as TextOverlay['design']) || 'classic';
      const captions = buildCaptionOverlaysFromCues(result.cues, { design });
      setTextOverlays((prev) => [...prev.filter((t) => t.source !== 'caption'), ...captions]);
    } catch (err: any) {
      if (hasScript) {
        populateCaptionsFromScript();
      } else {
        // Only surface the backend's message for a 4xx (an actionable reason like "too large"
        // / "too long"); a 5xx returns an opaque str(e), so fall back to the generic line.
        const status = err?.response?.status;
        const backendMsg =
          status >= 400 && status < 500 ? err?.response?.data?.error : undefined;
        setCaptionNotice(
          backendMsg ||
          "Couldn't read audio from this video for captions — add them on the timeline below.",
        );
      }
    } finally {
      setIsAnalyzingCaptions(false);
    }
  };

  const currentAssemblySignature = JSON.stringify({
    segs: timelineSegments.map((s) => `${s.media_id}:${s.time}:${s.duration}:${s.fade_in ?? 0}:${s.fade_out ?? 0}:${s.volume ?? 0}:${s.animation_in ?? ''}:${s.anim_in_duration ?? ''}:${s.animation_out ?? ''}:${s.anim_out_duration ?? ''}:${s.motion ?? ''}:${s.opacity ?? 100}`),
    music: resolvedBackgroundMusicUrl,
    musicVol: musicMixVolumePercent,
    captions: captionsEnabled,
    capDesign: captionDesign,
    capSpacing: captionSpacing,
    zoom: avatarScale,
    texts: textOverlays.map((t) => `${t.id}:${t.text}:${t.time}:${t.duration}:${t.style}:${t.position}:${t.animation ?? ''}:${t.anim_duration ?? ''}:${t.animation_out ?? ''}:${t.anim_out_duration ?? ''}:${t.design ?? ''}:${t.fontSize ?? ''}:${t.rotation ?? 0}:${t.color ?? ''}:${t.bold ?? false}:${t.italic ?? false}`),
  });
  const assemblyChangedSinceLastApply =
    assemblyAppliedSignatureRef.current !== currentAssemblySignature;

  // 5 steps when UGC_CUSTOM_IMAGES_ENABLED: Script → AI Model → Voice → Edit Video → Post Details.
  // 4 steps when disabled: Script → AI Model → Voice → Post Details.
  const lastStepNumber = UGC_CUSTOM_IMAGES_ENABLED ? 5 : 4;
  const isAddImagesStep = UGC_CUSTOM_IMAGES_ENABLED && activeConfigStep === 4;
  // Preview panel is hidden on the Script step (nothing to preview yet) and on the
  // Edit Video step (the editor renders its own preview).
  const showPreviewPanel = activeConfigStep !== 1 && !isAddImagesStep;
  const postDetailsStepNumber: 4 | 5 = UGC_CUSTOM_IMAGES_ENABLED ? 5 : 4;

  const handleScriptSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (scriptPrompt.trim() && !isGeneratingScript) {
      // Turn the length chip into an explicit word budget — "~30 sec" alone gives the
      // model nothing to size against, so scripts run long. The budget is appended
      // invisibly and also kept for the post-generation trim (see the streaming effect).
      const { seconds, targetWords, maxWords } = ugcScriptWordBudget(scriptLength);
      lastScriptMaxWordsRef.current = maxWords;
      const enrichedPrompt =
        `${scriptPrompt.trim()}\n\n` +
        `Length: about ${seconds} seconds spoken — roughly ${targetWords} words, ` +
        `and never more than ${maxWords}. Count the words and trim to fit before you finish.`;
      await onGenerateScript(enrichedPrompt);
      setScriptPrompt("");
    }
  };

  const handleGenerateVideo = () => {
    if (isOverLimit || !isReady || isGeneratingVideo || !onGenerateVideo) return;
    // Scale + vertical offset are applied downstream in Creatomate (avatar_zoom +
    // avatar_offset_y at assembly), NOT here — HeyGen renders neutral framing so the
    // preview lives in one CSS-reproducible coordinate system. Only style/matting,
    // which are HeyGen-only capabilities, go in avatar_layout.
    const avatarLayout: UGCAvatarLayout = {};
    // avatar_style / matting are not sent: HeyGen v3 has neither (SGL-497).
    const isAvatarIV = selectedEngine === 'avatar_iv';
    onGenerateVideo(cleanVoiceoverScript(transcriptForVideo), {
      video_format: selectedVideoFormat,
      aspect_ratio: VIDEO_FORMAT_TO_ASPECT_RATIO[selectedVideoFormat],
      resolution: selectedResolution,
      engine: selectedEngine,
      ...(Object.keys(avatarLayout).length > 0 ? { avatar_layout: avatarLayout } : {}),
      ...(isAvatarIV ? {
        expressiveness,
        ...(motionPrompt.trim() ? { motion_prompt: motionPrompt.trim() } : {}),
      } : {}),
    });
  };

  const handleSaveToReviewPosts = () => {
    if (generatedVideoUrl && onSaveToPlatform && selectedPlatforms.length > 0) {
      onSaveToPlatform(
        generatedVideoUrl,
        selectedPlatforms,
        transcriptForVideo || undefined,
        generatedCaption.trim() || undefined,
        tags.length > 0 ? tags : undefined,
        selectedPlatforms.includes(Platform.LINKEDIN_REELS) ? linkedinTarget : undefined
      );
    }
  };

  const getVoiceAccent = (voice: Voice) => {
    const value = (voice.language || '').toLowerCase();
    if (value.includes('uk') || value.includes('british')) return 'British';
    if (value.includes('australia')) return 'Australian';
    if (value.includes('india') || value.includes('indian')) return 'Indian';
    return 'Neutral';
  };

  const getVoiceTone = (voice: Voice) => {
    const key = (voice.voice_id || voice.name || '').toLowerCase();
    if (key.includes('emma') || key.includes('ava')) return 'Warm, natural, and professional.';
    if (key.includes('liam') || key.includes('ethan')) return 'Deep, engaging, and authoritative.';
    if (key.includes('zoe') || key.includes('mia')) return 'Friendly and approachable.';
    return 'Clear, confident, and conversational.';
  };

  const handlePlayVoiceListSample = async (voice: Voice) => {
    const url = voice.sample_url?.trim();
    if (!url) {
      debug.warn('No sample_url for voice:', voice.name);
      return;
    }

    const el = voiceSampleListAudioRef.current;
    if (!el) {
      debug.warn('Voice sample audio element not mounted');
      return;
    }

    if (playingVoiceSampleId === voice.voice_id) {
      el.pause();
      el.currentTime = 0;
      el.removeAttribute('src');
      el.load();
      setPlayingVoiceSampleId(null);
      return;
    }

    el.pause();
    el.onended = null;
    el.onerror = null;
    el.src = url;

    const onEnd = () => {
      setPlayingVoiceSampleId(null);
      if (voiceSampleListAudioRef.current) {
        voiceSampleListAudioRef.current.onended = null;
        voiceSampleListAudioRef.current.onerror = null;
      }
    };
    el.onended = onEnd;
    el.onerror = () => {
      if (el.error && el.error.code !== 1) {
        debug.error('Failed to play voice sample:', {
          code: el.error?.code,
          message: el.error?.message,
          url,
        });
      }
      onEnd();
    };

    try {
      await el.play();
      setPlayingVoiceSampleId(voice.voice_id);
    } catch (err: unknown) {
      const e = err as { name?: string };
      if (e?.name !== 'AbortError' && e?.name !== 'NotAllowedError') {
        debug.error('Audio play error:', err);
      }
      if (e?.name !== 'AbortError') {
        onEnd();
      }
    }
  };

  const filteredVoices = voices.filter((voice) => {
    // Tab split: AI tab shows catalog voices; Custom tab shows the customer's cloned voices.
    if (voiceTab === 'custom' ? !voice.is_custom : voice.is_custom) return false;
    const query = voiceSearchQuery.trim().toLowerCase();
    const matchesQuery =
      !query ||
      voice.name.toLowerCase().includes(query) ||
      voice.voice_id.toLowerCase().includes(query);
    if (voiceTab === 'custom') return matchesQuery; // language/gender/accent don't apply to clones
    const matchesLanguage =
      voiceLanguageFilter === "all" ||
      voice.language.toLowerCase().includes(voiceLanguageFilter.toLowerCase());
    const matchesGender =
      voiceGenderFilter === "all" ||
      voice.gender.toLowerCase() === voiceGenderFilter.toLowerCase();
    const accent = getVoiceAccent(voice).toLowerCase();
    const matchesAccent =
      voiceAccentFilter === "all" || accent === voiceAccentFilter.toLowerCase();
    return matchesQuery && matchesLanguage && matchesGender && matchesAccent;
  });

  // Custom tab: prepend just-cloned voices the backend list hasn't surfaced yet (GSI lag).
  const displayedVoices = voiceTab === 'custom'
    ? [
        ...recentCustomVoices.filter((rv) => !voices.some((v) => v.voice_id === rv.voice_id)),
        ...filteredVoices,
      ]
    : filteredVoices;

  /** Clone the user's voice from an uploaded audio sample, refresh the list, and select it. */
  const handleCustomVoiceFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !customerId) return;
    setCustomVoiceError(null);
    if (file.size > 10 * 1024 * 1024) {
      setCustomVoiceError('Audio file too large (max 10MB).');
      return;
    }
    if (file.size < 100 * 1024) {
      setCustomVoiceError('Sample too short — record at least ~30 seconds of clear speech.');
      return;
    }
    setIsUploadingVoice(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('Could not read the audio file.'));
        reader.readAsDataURL(file);
      });
      const name = customVoiceName.trim() || 'My Voice';
      const created = await uploadCustomVoice(customerId, name, base64, file.type || 'audio/mpeg', idToken);
      setCustomVoiceName('');
      const createdVoice: Voice = {
        voice_id: created.voice_id,
        name: created.name || name,
        language: 'Custom',
        gender: 'Custom',
        sample_url: created.sample_url,
        provider: 'ELEVENLABS',
        is_custom: true,
      };
      // Show it immediately (backend voice list is eventually consistent), then refresh.
      setRecentCustomVoices((prev) => [createdVoice, ...prev.filter((v) => v.voice_id !== createdVoice.voice_id)]);
      onSelectVoice(createdVoice);
      await onReloadVoices?.();
    } catch (err) {
      setCustomVoiceError(err instanceof Error ? err.message : 'Failed to upload voice. Please try again.');
    } finally {
      setIsUploadingVoice(false);
    }
  };

  // Show avatars with a usable thumbnail that hasn't failed to load, sorted by name.
  const sortedPublicAvatars = avatars
    .filter((av) => !!av.thumbnail_url && av.thumbnail_url.trim().length > 0)
    .filter((av) => !failedThumbnailIds.has(av.avatar_id))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  // Custom avatars: just-uploaded ones first (optimistic, until the backend list includes them).
  const displayedCustomAvatars = [
    ...recentCustomAvatars.filter((rv) => !customAvatarsProp.some((a) => a.avatar_id === rv.avatar_id)),
    ...customAvatarsProp,
  ];

  const isPanel = variant === 'panel';
  const isInsufficientCreditsError = typeof error === 'object' && error !== null && (error as any).type === 'INSUFFICIENT_CREDITS';
  const headerError = (isInsufficientCreditsError ? null : (error as string | null)) || videoError || assembly.assemblyError || testVideoUploadError;
  const wizardSteps = UGC_CUSTOM_IMAGES_ENABLED ? WIZARD_STEPS_5 : WIZARD_STEPS_4;
  return (
    <div
      className={
        isPanel
          ? 'flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-theme-surface'
          : 'fixed inset-0 z-50 flex h-dvh min-h-0 w-full max-h-dvh flex-col overflow-hidden bg-theme-surface'
      }
    >
      {/* ── TOP NAVIGATION BAR ── */}
      <div className="flex h-[60px] flex-shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4" onClick={e => e.stopPropagation()}>

        {/* Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: BRAND_TINT_BG }}>
            <svg className="h-4 w-4 text-brand-primary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <span className="text-[13px] font-semibold text-theme-fg">Video Creator</span>
        </div>

        {/* Step tabs */}
        <div className="flex flex-1 items-center justify-center">
          <div className="flex items-center">
            {wizardSteps.map((step, idx) => {
              const isActive = activeConfigStep === step.num;
              const isCompleted = activeConfigStep > step.num;
              return (
                <React.Fragment key={step.num}>
                  <button
                    type="button"
                    onClick={() => { if (isCompleted) setActiveConfigStep(step.num); }}
                    className={`flex items-center gap-1.5 rounded-lg px-2 py-1 transition-colors ${isCompleted ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'}`}
                  >
                    <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition-colors ${isActive ? 'bg-brand-primary text-white shadow-sm' : isCompleted ? 'bg-gray-300 text-white' : 'border-2 border-gray-200 text-gray-400 bg-white'}`}>
                      {step.num}
                    </span>
                    <span className={`text-[11px] font-medium ${isActive ? 'text-gray-900' : isCompleted ? 'text-gray-500' : 'text-gray-400'}`}>{step.label}</span>
                  </button>
                  {idx < wizardSteps.length - 1 && (
                    <div className={`h-px w-5 flex-shrink-0 ${isCompleted ? 'bg-gray-300' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-shrink-0 items-center gap-2">
          {/* Save Draft split-button */}
          <div className="relative" ref={draftMenuRef}>
            <div className="flex items-stretch rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isGeneratingVideo || assembly.isAssembling}
                className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={isGeneratingVideo || assembly.isAssembling ? 'Available once the video finishes generating' : 'Save Draft'}
              >
                {isSavingDraft ? (
                  <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                ) : (
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"/></svg>
                )}
                Save Draft
              </button>
              <button
                type="button"
                onClick={() => setIsDraftMenuOpen((v) => !v)}
                className="flex items-center px-1.5 py-1 text-gray-500 bg-white hover:bg-gray-50 border-l border-gray-200 transition-colors"
                title="Recent drafts"
              >
                <svg className={`h-3 w-3 transition-transform ${isDraftMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
              </button>
            </div>
            {isDraftMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 w-64 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                <div className="px-3 pt-2.5 pb-1">
                  <p className="text-[10px] font-semibold tracking-widest text-gray-400 uppercase">Recent Drafts</p>
                </div>
                <div className="border-t border-gray-100" />
                {isDraftsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <svg className="h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                  </div>
                ) : drafts.length === 0 ? (
                  <p className="px-3 py-4 text-center text-[12px] text-gray-400">No saved drafts yet.</p>
                ) : (
                  <>
                    {(showAllDrafts ? drafts : drafts.slice(0, 3)).map((record) => {
                      const updatedAt = new Date(record.updated_at);
                      const diffMs = Date.now() - updatedAt.getTime();
                      const diffMins = Math.floor(diffMs / 60000);
                      const relLabel = diffMins < 60
                        ? `Edited ${diffMins < 1 ? 'just now' : `${diffMins}m ago`}`
                        : diffMins < 1440
                          ? `Edited ${Math.floor(diffMins / 60)}h ago`
                          : `Edited ${Math.floor(diffMins / 1440)}d ago`;
                      return (
                        <div key={record.draft_id} className="group flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer" onClick={() => handleLoadDraft(record)}>
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-[12px] font-medium text-gray-800">{record.title}</p>
                            <p className="text-[10px] text-gray-400">{relLabel}</p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteDraft(record.draft_id).catch(() => showDraftToast('Failed to delete draft', true)); }}
                            className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                            title="Delete draft"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                          </button>
                        </div>
                      );
                    })}
                    {!showAllDrafts && drafts.length > 3 && (
                      <>
                        <div className="border-t border-gray-100" />
                        <button
                          type="button"
                          onClick={() => setShowAllDrafts(true)}
                          className="flex w-full items-center justify-between px-3 py-2 text-[12px] text-gray-500 hover:bg-gray-50 transition-colors"
                        >
                          <span>View all drafts</span>
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                        </button>
                      </>
                    )}
                    {showAllDrafts && drafts.length > 3 && (
                      <div className="border-t border-gray-100" />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          {isPanel && panelHeaderAccessory}
          {/* Preview Video — visible when video is ready */}
          {generatedVideoUrl && !isAddImagesStep && (
            <button
              type="button"
              onClick={() => { if (UGC_CUSTOM_IMAGES_ENABLED && activeConfigStep !== 4) setActiveConfigStep(4); }}
              className="flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
              Preview Video
            </button>
          )}
          {/* Finalize & Continue — shown on editor step (step 4) */}
          {UGC_CUSTOM_IMAGES_ENABLED && activeConfigStep === 4 && (
            <button
              type="button"
              onClick={handleApplyCustomImages}
              disabled={assembly.isAssembling}
              className="flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-[12px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {assembly.isAssembling ? (
                <>
                  <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                  Finalizing…
                </>
              ) : 'Finalize & Continue'}
            </button>
          )}
          {/* Close — confirm while generation/assembly is in-flight; the modal state
              survives close (videoGen lives in the parent) but it's still easy to
              accidentally lose the active view. */}
          <button
            type="button"
            onClick={() => {
              const busy = isGeneratingVideo || assembly.isAssembling;
              const inEditor = activeConfigStep === 4;
              if (busy) {
                setShowBusyCloseConfirm(true);
                return;
              }
              if (inEditor) {
                // Offer to save the work (incl. the generated video) as a draft instead of discarding it.
                setShowCloseDialog(true);
                return;
              }
              onClose();
            }}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <svg className="h-5 w-5 text-theme-fg-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showBusyCloseConfirm}
        title="Video creation is still running"
        message="Video creation is still running in the background. Close the window? You can reopen it any time from the VIDEO button to check progress."
        confirmLabel="Close window"
        onConfirm={() => {
          setShowBusyCloseConfirm(false);
          onClose();
        }}
        onCancel={() => setShowBusyCloseConfirm(false)}
      />

      {/* Save-as-draft-on-close dialog (replaces the lossy native confirm on the X button) */}
      {showCloseDialog && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
          onClick={() => setShowCloseDialog(false)}
        >
          <div
            className="mx-4 w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[15px] font-semibold text-gray-900">Save this video as a draft?</h3>
            <p className="mt-1.5 text-[12px] leading-relaxed text-gray-500">
              Your script, captions, scene edits, and the generated video will be saved so you can finish it later.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCloseDialog(false)}
                className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-gray-500 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDiscardAndClose}
                className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Don&apos;t save
              </button>
              <button
                type="button"
                onClick={handleSaveDraftAndClose}
                disabled={isSavingDraft}
                className="rounded-lg bg-brand-primary px-3.5 py-1.5 text-[12px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {isSavingDraft ? 'Saving…' : 'Save as draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draft toast */}
      {draftToast && (
        <div
          className={`fixed bottom-4 left-1/2 z-[9999] -translate-x-1/2 flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-medium text-white shadow-lg pointer-events-none ${draftToast.isError ? 'bg-red-500' : 'bg-gray-800'}`}
          style={{ maxWidth: '90%' }}
        >
          {draftToast.isError ? (
            <svg className="h-3.5 w-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
          ) : (
            <svg className="h-3.5 w-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
          )}
          {draftToast.message}
        </div>
      )}

      {/* Error banner */}
      {videoError === 'INSUFFICIENT_CREDITS' ? (
        <div className="flex flex-shrink-0 items-start gap-3 border-b border-orange-200 bg-orange-50 px-5 py-3" role="alert">
          <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="text-xs font-semibold text-orange-800">Out of Credits</p>
            <p className="mt-0.5 text-xs text-orange-700">You've run out of credits. Add funds to continue creating videos.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/billing')}
            className="flex-shrink-0 rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 transition-colors"
          >
            Add Credits
          </button>
        </div>
      ) : headerError && (
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-amber-200 bg-amber-50 px-5 py-2.5" role="alert">
          <svg className="h-4 w-4 flex-shrink-0 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <p className="flex-1 text-xs text-amber-800">{headerError}</p>
          {onRetryCheck && headerError.includes('Check again') && (
            <button type="button" onClick={onRetryCheck} className="flex-shrink-0 rounded-md bg-theme-accent-warm px-3 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90">
              Check
            </button>
          )}
        </div>
      )}

      {/* Preview + main editor — modern 65/35 split layout */}
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {/* LEFT PANEL - Video Preview (65% width, dominant). Hidden on Script step (no
            video yet) and Edit Video step (editor owns its own preview). */}
        {showPreviewPanel && <div className="flex flex-1 min-h-0 flex-col border-r border-theme-border bg-theme-surface">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-theme-surface">
            {/* Phone frame — fills all available height */}
            <div className="flex min-h-0 flex-1 items-center justify-center p-4">
            <div
              className="relative h-full max-h-full overflow-hidden rounded-[2rem] border border-[#E5E5E5] bg-[#1a1a1a] shadow-2xl group"
              style={{ aspectRatio: selectedVideoFormatOption.aspectRatio, maxWidth: '100%' }}
            >
                {generatedVideoUrl ? (
                  <>
                    <div
                      className="absolute inset-0 z-0 overflow-hidden bg-[#0a0a0a]"
                    >
                      <video
                        key={generatedVideoUrl}
                        ref={previewVideoRef}
                        src={generatedVideoUrl}
                        playsInline
                        style={avatarPreviewMediaStyle(avatarScale, avatarOffsetY)}
                        onPlay={() => setPreviewVideoPlaying(true)}
                        onPause={() => setPreviewVideoPlaying(false)}
                        onTimeUpdate={() => setPreviewCurrentTime(previewVideoRef.current?.currentTime ?? 0)}
                        onLoadedData={() => {
                          setVideoLoadError(null);
                          setPreviewCurrentTime(previewVideoRef.current?.currentTime ?? 0);
                        }}
                        onSeeked={() => setPreviewCurrentTime(previewVideoRef.current?.currentTime ?? 0)}
                        onError={() => setVideoLoadError('Video failed to load in this player')}
                      />
                    </div>
                    {/* Play / pause tap overlay */}
                    <button
                      type="button"
                      onClick={() => previewVideoPlaying ? previewVideoRef.current?.pause() : previewVideoRef.current?.play()}
                      className="absolute inset-0 z-[3] flex items-center justify-center bg-transparent"
                      aria-label={previewVideoPlaying ? 'Pause' : 'Play'}
                    >
                      {!previewVideoPlaying && (
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm">
                          <svg className="ml-1 h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      )}
                    </button>
                    {/* Local HTML overlays for media segments — shown only BEFORE assembly so the
                        user can preview their work without re-rendering. After assembly succeeds
                        (currentAssemblySignature has been applied), the overlays are baked into
                        the Creatomate video URL itself; rendering them again here would double
                        them visually and mask cases where Creatomate didn't actually include them. */}
                    {timelineSegments.length > 0 && assemblyAppliedSignatureRef.current !== currentAssemblySignature && (
                      <div className="absolute inset-0 z-[1] pointer-events-none" aria-hidden>
                        {timelineSegments.map((seg) => {
                          const inRange = previewCurrentTime >= seg.time && previewCurrentTime < seg.time + seg.duration;
                          const isVideoSeg = seg.segment_type === 'video';
                          const videoUrl = isVideoSeg ? segmentVideoUrlsMap[seg.media_id] : undefined;
                          const fitClass = seg.fit === 'contain' ? 'object-contain' : 'object-cover';
                          if (isVideoSeg && videoUrl) {
                            return (
                              <video
                                key={seg.media_id}
                                src={videoUrl}
                                autoPlay
                                muted
                                loop
                                playsInline
                                className={`absolute inset-0 w-full h-full ${fitClass}`}
                                style={{ display: inRange ? 'block' : 'none' }}
                                ref={(el) => {
                                  if (!el) return;
                                  if (inRange && el.paused) el.play().catch(() => {});
                                  else if (!inRange && !el.paused) el.pause();
                                }}
                              />
                            );
                          }
                          const thumbUrl = segmentThumbnailsMap[seg.media_id];
                          if (!thumbUrl) return null;
                          return (
                            <img
                              key={seg.media_id}
                              src={thumbUrl}
                              alt=""
                              className={`absolute inset-0 w-full h-full ${fitClass}`}
                              style={{ display: inRange ? 'block' : 'none' }}
                            />
                          );
                        })}
                      </div>
                    )}
                    {textOverlays.length > 0 && assemblyAppliedSignatureRef.current !== currentAssemblySignature && (
                      <div className="absolute inset-0 z-[2] pointer-events-none" aria-hidden>
                        {textOverlays.map((overlay) => {
                          const inRange = previewCurrentTime >= overlay.time && previewCurrentTime < overlay.time + overlay.duration;
                          if (!inRange) return null;
                          const posClass = overlay.position === 'top' ? 'top-6' : overlay.position === 'center' ? 'top-1/2 -translate-y-1/2' : 'bottom-6';
                          const textSizeClass = overlay.style === 'title' ? 'text-lg font-bold' : overlay.style === 'cta' ? 'text-sm font-bold' : overlay.style === 'lower-third' ? 'text-xs font-semibold' : 'text-sm font-medium';
                          return (
                            <div key={overlay.id} className={`absolute left-0 right-0 flex justify-center px-4 ${posClass}`}>
                              <span className={`rounded bg-black/60 px-3 py-1.5 text-white text-center leading-snug ${textSizeClass}`}>
                                {overlay.text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {videoLoadError && (
                      <div className="absolute inset-0 z-[2] bg-black/70 flex flex-col items-center justify-center gap-3 p-4 text-center">
                        <span className="text-xs text-white/90">{videoLoadError}</span>
                        <a
                          href={generatedVideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-[#2f4941] hover:underline"
                        >
                          Open video in new tab
                        </a>
                      </div>
                    )}
                    {!previewVideoPlaying && !videoLoadError && (
                      <button
                        type="button"
                        onClick={async () => {
                          const video = previewVideoRef.current;
                          if (!video) return;
                          try {
                            await video.play();
                          } catch (err) {
                            setVideoLoadError('Cannot play here. Open the link below in a new tab.');
                          }
                        }}
                        className="absolute inset-0 z-[2] bg-black/20 flex flex-col items-center justify-center gap-2 text-white/90 hover:bg-black/30 transition-colors"
                      >
                        <div className="w-14 h-14 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center border border-white/30">
                          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                        <span className="text-[10px] font-medium uppercase tracking-wider">Play</span>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {/* Pre-generation preview: the selected avatar's thumbnail standing in for
                        the eventual video. Same avatar_layout transform + style as the video
                        branch so adjusting Scale/Offset/Style is immediately visible here too. */}
                    <div
                      className="absolute inset-0 z-0 overflow-hidden bg-[#1a1a1a]"
                    >
                      {selectedAvatar?.thumbnail_url && (
                        <img
                          src={selectedAvatar.thumbnail_url}
                          alt={selectedAvatar.name}
                          style={avatarPreviewMediaStyle(avatarScale, avatarOffsetY)}
                        />
                      )}
                    </div>
                    {isGeneratingVideo && (
                      <>
                        <div className="absolute inset-0 z-[2] bg-black/30 flex flex-col items-center justify-center gap-2">
                          <div className="w-14 h-14 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center text-white/80 border border-white/30">
                            <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          </div>
                          <span className="text-[10px] font-medium text-white/90 uppercase tracking-wider">
                            {videoStatus ?? 'Generating'}
                          </span>
                        </div>
                        <div className="absolute bottom-6 left-6 right-6 z-[2] px-4 py-2 rounded-lg bg-black/40 backdrop-blur-sm">
                          <div className="text-[11px] font-medium text-white/90">
                            {Math.floor(elapsedSeconds / 60)}:{(elapsedSeconds % 60).toString().padStart(2, '0')} elapsed
                          </div>
                          <div className="text-[10px] text-white/70 mt-0.5">
                            May take up to 10 minutes
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
            </div>
            </div>
            {/* Footer: format badge + download. Zoom moved to the Avatar layout card under
                Quality settings — single source of truth, no duplicate knob here. */}
            <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-[#E5E5E5] px-4 py-2.5">
              <span className="rounded bg-[#F5F5F5] border border-[#E5E5E5] px-2.5 py-1 text-xs font-medium text-[#333333]">
                {selectedVideoFormatOption.badge}
              </span>
              {generatedVideoUrl && (
                <button
                  type="button"
                  onClick={handleDownloadVideo}
                  disabled={isDownloading}
                  className="flex items-center gap-2 rounded-lg border border-[#E5E5E5] bg-white px-3 py-1.5 text-sm font-medium text-[#333333] hover:bg-[#F5F5F5] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isDownloading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Save to computer
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>}

        {/* MIDDLE PANEL - Scene Configuration (flex-1 to fill space) */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-r border-theme-border bg-theme-surface">
          {/* Step content - full height (order: Script → AI Model → Voice → Edit Video; Generate Video on last step only) */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Shared hidden file input for the "upload your own" paths (start picker + script screen). */}
            <input
              ref={testVideoFileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={handleTestVideoFileChange}
            />

            {/* STEP 1a: how do you want to start — generate with AI, or upload your own video. */}
            {activeConfigStep === 1 && startChoice === null && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[#F9FAFB]">
                <div className="flex-1 overflow-y-auto px-8 py-12">
                  <div className="max-w-2xl mx-auto text-center mb-10">
                    <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center">
                      <svg className="h-6 w-6" style={{ color: '#C9A86C' }} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l1.6 4.4L18 8l-4.4 1.6L12 14l-1.6-4.4L6 8l4.4-1.6L12 2zm6.5 9l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6zM6 13l.7 1.9 1.8.6-1.8.7L6 18l-.7-1.8L3.5 15.5l1.8-.6L6 13z" />
                      </svg>
                    </div>
                    <h1 className="text-2xl font-semibold text-[#1F2937]">Create your video</h1>
                    <p className="mt-2 text-sm text-[#6B7280]">
                      Choose how you want to make your video. You can always switch later.
                    </p>
                  </div>

                  <div className="mx-auto grid max-w-3xl gap-5 md:grid-cols-2">
                    {/* Generate with AI — sage/green identity */}
                    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-colors hover:border-[#2f4941]/30">
                      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: BRAND_TINT_BG }}>
                        <svg className="h-5 w-5 text-[#2f4941]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L23 12l-6.857 2.143L14 21l-2.143-6.857L5 12l6.857-2.143L14 3z" />
                        </svg>
                      </div>
                      <h2 className="text-base font-semibold text-[#1F2937]">Generate with AI</h2>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-[#6B7280]">
                        Create a video with an AI avatar and voice. Write your own script or let AI draft it.
                      </p>
                      <ul className="mt-4 space-y-2">
                        {[
                          { label: 'Available avatars and voices', d: 'M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v6H4zM14 15h6v6h-6z' },
                          { label: 'Or use your own avatar and voice', d: 'M15 7a4 4 0 11-8 0 4 4 0 018 0zM5 21v-1a6 6 0 0110.9-3.45M18 14v6m3-3h-6' },
                          { label: 'AI-written or your own script', d: 'M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 9h6M9 13h6M9 17h4' },
                        ].map((f) => (
                          <li key={f.label} className="flex items-center gap-2.5 text-[12px] text-[#374151]">
                            <svg className="h-4 w-4 flex-shrink-0 text-[#2f4941]" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={f.d} /></svg>
                            {f.label}
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={() => setStartChoice('ai')}
                        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#2f4941] py-2.5 text-sm font-semibold text-white transition-all hover:brightness-95"
                      >
                        Generate with AI
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>

                    {/* Upload your own — warm Sagelyn "ribbon gold" identity */}
                    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-colors hover:border-[#C9A86C]/50">
                      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: WARM_TINT_BG }}>
                        <svg className="h-5 w-5" style={{ color: '#9A7B46' }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6.002 4.5 4.5 0 0117 15M12 12v9m0-9l-3.5 3.5M12 12l3.5 3.5" />
                        </svg>
                      </div>
                      <h2 className="text-base font-semibold text-[#1F2937]">Upload your own</h2>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-[#6B7280]">
                        Bring a video you already have and edit it with captions, music, and simple tools.
                      </p>
                      <ul className="mt-4 space-y-2">
                        {[
                          { label: 'Upload from your device', d: 'M12 15V3m0 0L8 7m4-4l4 4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2' },
                          { label: 'Edit with simple tools', d: 'M4 20h4L18.5 9.5a2.12 2.12 0 00-3-3L5 17v3zM13.5 6.5l4 4' },
                          { label: 'Add captions, music, and more', d: 'M9 18V6l12-3v11M9 18a3 3 0 11-6 0 3 3 0 016 0zM21 14a3 3 0 11-6 0 3 3 0 016 0z' },
                        ].map((f) => (
                          <li key={f.label} className="flex items-center gap-2.5 text-[12px] text-[#374151]">
                            <svg className="h-4 w-4 flex-shrink-0" style={{ color: '#9A7B46' }} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={f.d} /></svg>
                            {f.label}
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={() => { setUploadSource('device'); setStartChoice('upload'); }}
                        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-semibold text-[#1F2937] transition-colors hover:bg-[#FBF8F1]"
                        style={{ borderColor: '#C9A86C' }}
                      >
                        Upload your own
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                  </div>

                  {testVideoUploadError && (
                    <p className="mx-auto mt-4 max-w-3xl text-center text-[12px] text-red-600">{testVideoUploadError}</p>
                  )}
                </div>
              </div>
            )}

            {/* STEP 1b: Upload your own — from this device, or from your video library. */}
            {activeConfigStep === 1 && startChoice === 'upload' && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[#F9FAFB]">
                <div className="flex-1 overflow-y-auto px-8 py-12">
                  <div className="max-w-3xl mx-auto">
                    <button
                      type="button"
                      onClick={() => setStartChoice(null)}
                      disabled={isUploadingTestVideo}
                      className="mb-4 inline-flex items-center gap-1 text-xs font-medium text-[#6B7280] hover:text-[#374151] disabled:opacity-60"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                      Back
                    </button>

                    <h1 className="text-2xl font-semibold text-[#1F2937]">Upload your video</h1>
                    <p className="mt-2 text-sm text-[#6B7280]">Upload a video, or reuse one from your library.</p>

                    {/* Source toggle */}
                    <div className="mt-5 inline-grid grid-cols-2 gap-1 rounded-xl border border-gray-200 bg-white p-1">
                      {([
                        { id: 'device', label: 'Upload' },
                        { id: 'library', label: 'Your library' },
                      ] as const).map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setUploadSource(s.id)}
                          disabled={isUploadingTestVideo}
                          aria-pressed={uploadSource === s.id}
                          className={`rounded-lg px-5 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
                            uploadSource === s.id ? 'bg-[#2f4941] text-white shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>

                    {/* From this device */}
                    {uploadSource === 'device' && (
                      <button
                        type="button"
                        onClick={() => testVideoFileInputRef.current?.click()}
                        disabled={isUploadingTestVideo}
                        className="mt-5 flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 bg-white px-6 py-12 text-center transition-colors hover:border-[#C9A86C] hover:bg-[#FBF8F1] disabled:opacity-60"
                      >
                        {isUploadingTestVideo ? (
                          <>
                            <svg className="h-6 w-6 animate-spin text-[#9A7B46]" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                            <span className="text-sm font-semibold text-[#1F2937]">Uploading your video…</span>
                          </>
                        ) : (
                          <>
                            <svg className="h-7 w-7" style={{ color: '#9A7B46' }} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15V3m0 0L8 7m4-4l4 4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
                            <span className="text-sm font-semibold text-[#1F2937]">Choose a video file</span>
                            <span className="text-[11px] text-[#9CA3AF]">MP4, MOV or WebM</span>
                          </>
                        )}
                      </button>
                    )}

                    {/* From your library */}
                    {uploadSource === 'library' && (
                      <div className="mt-5">
                        {libraryVideosLoading ? (
                          <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white py-16">
                            <svg className="h-5 w-5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                          </div>
                        ) : libraryVideosError ? (
                          <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center">
                            <p className="text-[13px] text-red-600">{libraryVideosError}</p>
                            <button
                              type="button"
                              onClick={() => void loadLibraryVideos()}
                              className="mt-2 text-[12px] font-medium text-[#2f4941] underline underline-offset-2"
                            >
                              Try again
                            </button>
                          </div>
                        ) : (libraryVideos && libraryVideos.length > 0) ? (
                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {libraryVideos.map((item) => {
                              const url = (item.cloudfront_url || getMediaThumbnailUrl(item) || '').trim();
                              return (
                                <button
                                  key={item.media_id}
                                  type="button"
                                  onClick={() => handleLibraryVideoPick(item)}
                                  disabled={isUploadingTestVideo || !url}
                                  className="group relative aspect-video overflow-hidden rounded-xl border border-gray-200 bg-gray-100 transition-all hover:border-[#C9A86C] hover:shadow-sm disabled:opacity-50"
                                >
                                  <video src={url ? `${url}#t=0.1` : undefined} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                                    <svg className="h-8 w-8 text-white opacity-0 drop-shadow transition-opacity group-hover:opacity-100" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center">
                            <p className="text-[13px] text-[#6B7280]">No videos in your library yet.</p>
                            <button
                              type="button"
                              onClick={() => setUploadSource('device')}
                              className="mt-2 text-[12px] font-medium text-[#2f4941] underline underline-offset-2"
                            >
                              Upload one from this device
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {testVideoUploadError && (
                      <p className="mt-4 text-[12px] text-red-600">{testVideoUploadError}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 1: Script — single centered AI-generation card. */}
            {activeConfigStep === 1 && startChoice === 'ai' && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[#F9FAFB]">
                <div className="flex-1 overflow-y-auto px-8 py-12">
                  {/* Back to the start picker (only before any script exists) */}
                  {!transcriptForVideo && (
                    <div className="max-w-xl mx-auto mb-4">
                      <button
                        type="button"
                        onClick={() => setStartChoice(null)}
                        disabled={isGeneratingScript || isUploadingTestVideo}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#6B7280] hover:text-[#374151] disabled:opacity-60"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        Back
                      </button>
                    </div>
                  )}
                  {/* Hero */}
                  <div className="max-w-xl mx-auto text-center mb-6">
                    <h1 className="text-2xl font-semibold text-[#1F2937]">
                      {scriptMode === 'manual' ? 'Write your video script' : 'Let AI write your video script'}
                    </h1>
                    <p className="mt-2 text-sm text-[#6B7280]">
                      {scriptMode === 'manual'
                        ? 'Type the exact words your avatar will say.'
                        : 'Describe your video and we’ll generate the script.'}
                    </p>
                  </div>

                  {/* Mode toggle: let AI write it, or write your own */}
                  <div className="max-w-xl mx-auto mb-5">
                    <div className="grid grid-cols-2 gap-1 rounded-xl border border-gray-200 bg-white p-1">
                      {([
                        { id: 'ai', label: 'Generate with AI' },
                        { id: 'manual', label: 'Write my own' },
                      ] as const).map((m) => {
                        const isActive = scriptMode === m.id;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => setScriptMode(m.id)}
                            disabled={isGeneratingScript}
                            aria-pressed={isActive}
                            className={`rounded-lg py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                              isActive ? 'bg-[#2f4941] text-white shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'
                            }`}
                          >
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Input card */}
                  {scriptMode === 'ai' && (
                  <form onSubmit={handleScriptSubmit} className="max-w-xl mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <div className="relative mb-4">
                      <textarea
                        value={scriptPrompt}
                        onChange={(e) => setScriptPrompt(e.target.value.slice(0, 200))}
                        placeholder="Describe your script here…"
                        disabled={isGeneratingScript}
                        rows={3}
                        className="w-full resize-none rounded-lg border border-[#E5E5E5] bg-white px-4 py-3 pb-7 text-sm text-[#333333] focus:ring-2 focus:ring-[#2f4941]/30 focus:border-[#2f4941] disabled:opacity-60"
                      />
                      <span className="pointer-events-none absolute bottom-2.5 right-4 text-[10px] font-mono text-[#9CA3AF]">{scriptPrompt.length} / 200</span>
                    </div>

                    {/* Length chip — custom dropdown */}
                    <div className="mb-5 flex items-center justify-between">
                      <div ref={lengthMenuRef} className="relative">
                        <button
                          type="button"
                          onClick={() => setIsLengthMenuOpen((o) => !o)}
                          aria-haspopup="listbox"
                          aria-expanded={isLengthMenuOpen}
                          className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-white pl-3 pr-3 py-1.5 text-[12px] text-[#374151] transition-colors hover:border-gray-300 focus:outline-none focus-visible:border-[#2f4941] focus-visible:ring-2 focus-visible:ring-[#2f4941]/20"
                        >
                          <svg className="h-3.5 w-3.5 shrink-0 text-[#6B7280]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" /></svg>
                          <span className="font-medium">Length:</span>
                          <span className="font-semibold text-[#1F2937]">{scriptLength}</span>
                          <span className="text-[#9CA3AF]">· ~{ugcScriptWordBudget(scriptLength).targetWords} words</span>
                          <svg className={`ml-0.5 h-3 w-3 text-[#9CA3AF] transition-transform duration-150 ${isLengthMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                        </button>
                        {isLengthMenuOpen && (
                          <ul
                            role="listbox"
                            aria-label="Target script length"
                            className="absolute left-0 top-full z-20 mt-1.5 min-w-[188px] overflow-hidden rounded-xl border border-gray-200 bg-white p-1 shadow-lg shadow-black/5 ring-1 ring-black/5"
                          >
                            {UGC_SCRIPT_LENGTHS.map((opt) => {
                              const isSelected = scriptLength === opt;
                              return (
                                <li key={opt}>
                                  <button
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={() => {
                                      setScriptLength(opt);
                                      setIsLengthMenuOpen(false);
                                    }}
                                    className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-[12px] transition-colors ${
                                      isSelected
                                        ? 'bg-[#2f4941]/8 font-semibold text-[#2f4941]'
                                        : 'text-[#374151] hover:bg-gray-50'
                                    }`}
                                  >
                                    <span className="whitespace-nowrap">{opt} <span className="font-normal text-[#9CA3AF]">· ~{ugcScriptWordBudget(opt).targetWords} words</span></span>
                                    {isSelected && (
                                      <svg className="h-3.5 w-3.5 shrink-0 text-[#2f4941]" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                    )}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={!scriptPrompt.trim() || isGeneratingScript}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#2f4941] py-3 text-sm font-semibold text-white hover:brightness-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGeneratingScript ? 'Generating…' : 'Generate Script'}
                    </button>

                    {isInsufficientCreditsError && (
                      <div className="mt-3 flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
                        <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-orange-800">Out of Credits</p>
                          <p className="mt-0.5 text-sm text-orange-700">You've run out of credits. Add funds to continue generating scripts.</p>
                          <button
                            type="button"
                            onClick={() => navigate('/billing')}
                            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 transition-colors"
                          >
                            Add Credits
                          </button>
                        </div>
                      </div>
                    )}
                  </form>
                  )}

                  {/* Write-your-own script card */}
                  {scriptMode === 'manual' && (
                    <div className="max-w-xl mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                      <label htmlFor="ugc-manual-script" className="mb-2 block text-sm font-semibold text-[#1F2937]">
                        Your script
                      </label>
                      <div className="relative">
                        <textarea
                          id="ugc-manual-script"
                          value={localScriptContent}
                          onChange={(e) => {
                            const val = e.target.value;
                            setLocalScriptContent(val);
                            setScriptContent(val);
                          }}
                          placeholder="Write the exact words your avatar will say…"
                          rows={8}
                          className={`w-full min-h-[200px] resize-y rounded-lg border px-4 py-3 pb-7 text-sm leading-relaxed text-[#333333] focus:outline-none ${
                            isOverLimit
                              ? 'border-red-500 focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
                              : 'border-[#E5E5E5] focus:ring-2 focus:ring-[#2f4941]/30 focus:border-[#2f4941]'
                          }`}
                        />
                        <span className={`pointer-events-none absolute bottom-2.5 right-4 text-[10px] font-mono ${isOverLimit ? 'text-red-600' : 'text-[#9CA3AF]'}`}>
                          {isOverLimit ? `${overBy} over` : `${transcriptForVideo.length} / ${SCRIPT_CHAR_LIMIT}`}
                        </span>
                      </div>
                      {localScriptContent.trim() && (
                        <div className="mt-3 flex justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              setLocalScriptContent('');
                              setScriptContent('');
                            }}
                            className="text-[11px] font-medium text-[#6B7280] hover:text-[#374151] underline underline-offset-2"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Streaming preview while AI types */}
                  {isGeneratingScript && streamingScriptContent && (
                    <div className="max-w-xl mx-auto mt-6 rounded-xl border border-[#E5E5E5] bg-white shadow-sm p-4 max-h-40 overflow-y-auto">
                      <p className="text-sm text-[#333333] whitespace-pre-wrap">
                        {streamingScriptContent}
                        <span className="inline-block w-2 h-4 bg-[#2f4941] ml-1 animate-pulse" />
                      </p>
                    </div>
                  )}

                  {/* Generated script editor (collapsible, only after generation) */}
                  {scriptMode === 'ai' && localScriptContent.trim() && !isGeneratingScript && (
                    <details className="max-w-xl mx-auto mt-6 rounded-xl border border-gray-200 bg-white shadow-sm group" open>
                      <summary className="flex cursor-pointer items-center justify-between gap-2 px-5 py-3 list-none [&::-webkit-details-marker]:hidden">
                        <div className="flex items-center gap-2 text-sm font-semibold text-[#1F2937]">
                          <svg className="w-4 h-4 text-[#2f4941]" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          Script ready
                          <span className={`text-xs font-mono font-normal ${isOverLimit ? 'text-red-600' : 'text-[#6B7280]'}`}>· {isOverLimit ? `-${overBy} over` : `${transcriptForVideo.length} characters`}</span>
                        </div>
                        <svg className="w-4 h-4 text-[#9CA3AF] transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                      </summary>
                      <div className="px-5 pb-5">
                        <textarea
                          ref={scriptTextareaRef}
                          value={localScriptContent}
                          onChange={(e) => {
                            const val = e.target.value;
                            setLocalScriptContent(val);
                            setScriptContent(val);
                          }}
                          className={`min-h-[180px] w-full px-4 py-3 rounded-lg border resize-y text-sm leading-relaxed text-[#333333] ${
                            isOverLimit
                              ? 'border-red-500 focus:ring-2 focus:ring-red-500/20 focus:border-red-500'
                              : 'border-[#E5E5E5] focus:ring-2 focus:ring-[#2f4941]/30 focus:border-[#2f4941]'
                          }`}
                        />
                        <div className="mt-3 flex justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              setLocalScriptContent("");
                              setScriptContent("");
                            }}
                            className="text-[11px] font-medium text-[#6B7280] hover:text-[#374151] underline underline-offset-2"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </details>
                  )}
                </div>

                {/* Bottom bar */}
                <div className="flex-shrink-0 border-t border-gray-200 bg-white px-8 py-4 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setActiveConfigStep(2)}
                    className="px-6 py-2.5 bg-[#2f4941] text-white rounded-lg text-sm font-semibold hover:brightness-95 transition-all flex items-center gap-2"
                  >
                    Next: Scenes
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: AI Model (Avatar) */}
            {activeConfigStep === 2 && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex-1 min-h-0 overflow-y-auto px-8 pt-8">
                  {/* Video Format — picked before the avatar so the preview canvas matches the target
                      aspect ratio. Collapsed by default to keep the avatar grid front-and-centre. */}
                  <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 px-3.5 py-2.5 mb-2">
                    <button
                      type="button"
                      onClick={() => setVideoFormatExpanded((v) => !v)}
                      className="flex w-full items-center justify-between gap-3 text-left"
                      aria-expanded={videoFormatExpanded}
                    >
                      <div className="min-w-0">
                        <span className="text-[13px] font-semibold text-[#333333]">Video format</span>
                        <p className="truncate text-[11px] text-[#333333]/60">
                          {videoFormatExpanded
                            ? 'Match the canvas to your target channel.'
                            : `${selectedVideoFormatOption.label} · ${selectedVideoFormatOption.helper}`}
                        </p>
                      </div>
                      <span className="flex flex-shrink-0 items-center gap-1.5">
                        <span className="rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[10px] font-medium text-[#333333]">
                          {selectedVideoFormatOption.badge}
                        </span>
                        <svg
                          className={`h-3.5 w-3.5 text-[#9CA3AF] transition-transform ${videoFormatExpanded ? 'rotate-180' : ''}`}
                          fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>
                    {videoFormatExpanded && (
                      <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {VIDEO_FORMAT_OPTIONS.map((option) => {
                          const selected = selectedVideoFormat === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setSelectedVideoFormat(option.value)}
                              className={`rounded-lg border px-3 py-3 text-left transition-all ${
                                selected
                                  ? 'border-[#B7793B] bg-[#FFF8F1] ring-2 ring-[#B7793B]/15'
                                  : 'border-[#E5E5E5] bg-[#FAFAFA] hover:border-[#D1D5DB]'
                              }`}
                            >
                              <span className="block text-sm font-semibold text-[#1F2937]">{option.label}</span>
                              <span className="mt-0.5 block text-[11px] font-mono text-[#6B7280]">{option.badge}</span>
                              <span className="mt-1 block text-[11px] leading-snug text-[#6B7280]">{option.helper}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Quality Settings — engine, resolution, expressiveness, motion prompt.
                      Collapsed by default (advanced/optional) to keep the avatar grid front-and-centre. */}
                  <div className="mb-3 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2.5">
                    <button
                      type="button"
                      onClick={() => setQualitySettingsExpanded((v) => !v)}
                      className="flex w-full items-center justify-between gap-3 text-left"
                      aria-expanded={qualitySettingsExpanded}
                    >
                      <div className="min-w-0">
                        <h3 className="text-[13px] font-semibold text-[#1F2937]">Quality settings</h3>
                        <p className="truncate text-[11px] leading-relaxed text-[#6B7280]">
                          {qualitySettingsExpanded
                            ? 'Engine, resolution, and avatar motion.'
                            : `${selectedEngineOption?.label ?? 'Default engine'} · ${selectedResolution === '4k' ? '4K' : selectedResolution.toUpperCase()}`}
                        </p>
                      </div>
                      <span className="flex flex-shrink-0 items-center gap-1.5">
                        <span className="rounded-full bg-[#F9FAFB] px-2 py-0.5 text-[10px] text-[#6B7280]">Optional</span>
                        <svg
                          className={`h-3.5 w-3.5 text-[#9CA3AF] transition-transform ${qualitySettingsExpanded ? 'rotate-180' : ''}`}
                          fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>

                    {qualitySettingsExpanded && (
                    <div className="mt-3">
                    {/* Engine */}
                    <div className="mb-4">
                      <p className="mb-2 text-xs font-medium text-[#374151]">Rendering engine</p>
                      <div className="grid grid-cols-2 gap-2">
                        {ENGINE_OPTIONS.map((opt) => {
                          const sel = selectedEngine === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setSelectedEngine(opt.value)}
                              className={`rounded-lg border px-3 py-2.5 text-left transition-all ${sel ? 'border-[#B7793B] bg-[#FFF8F1] ring-2 ring-[#B7793B]/15' : 'border-[#E5E5E5] bg-[#FAFAFA] hover:border-[#D1D5DB]'}`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-[#1F2937]">{opt.label}</span>
                                <span className="text-[10px] rounded-full bg-[#F3F4F6] px-1.5 py-0.5 text-[#6B7280]">{opt.badge}</span>
                              </div>
                              <span className="mt-0.5 block text-[11px] leading-snug text-[#6B7280]">{opt.helper}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Resolution */}
                    <div className="mb-4">
                      <p className="mb-2 text-xs font-medium text-[#374151]">Output resolution</p>
                      <div className="flex flex-wrap gap-2">
                        {(['720p', '1080p'] as UGCVideoResolution[]).map((res) => (
                          <button
                            key={res}
                            type="button"
                            onClick={() => setSelectedResolution(res)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${selectedResolution === res ? 'border-[#B7793B] bg-[#FFF8F1] text-[#2f4941]' : 'border-[#E5E7EB] bg-[#F9FAFB] text-[#6B7280] hover:border-[#D1D5DB]'}`}
                          >
                            {res === '4k' ? '4K' : res.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Expressiveness — Avatar IV only */}
                    {selectedEngine === 'avatar_iv' && (
                      <div className="mb-4">
                        <p className="mb-2 text-xs font-medium text-[#374151]">Motion intensity <span className="text-[#9CA3AF] font-normal">(Avatar IV only)</span></p>
                        <div className="flex flex-wrap gap-2">
                          {EXPRESSIVENESS_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setExpressiveness(opt.value)}
                              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${expressiveness === opt.value ? 'border-[#B7793B] bg-[#FFF8F1] text-[#2f4941]' : 'border-[#E5E7EB] bg-[#F9FAFB] text-[#6B7280] hover:border-[#D1D5DB]'}`}
                              title={opt.helper}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Motion prompt — Avatar IV only */}
                    {selectedEngine === 'avatar_iv' && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-xs font-medium text-[#374151]">Motion prompt <span className="text-[#9CA3AF] font-normal">(optional)</span></p>
                          <span className={`font-mono text-[10px] ${motionPrompt.length > MOTION_PROMPT_CHAR_LIMIT ? 'text-red-500' : 'text-[#9CA3AF]'}`}>{motionPrompt.length}/{MOTION_PROMPT_CHAR_LIMIT}</span>
                        </div>
                        <textarea
                          value={motionPrompt}
                          onChange={(e) => setMotionPrompt(e.target.value.slice(0, MOTION_PROMPT_CHAR_LIMIT))}
                          placeholder="Describe desired gestures or body motion, e.g. 'Confident presenter with open hand gestures…'"
                          rows={2}
                          className="w-full resize-none rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-xs text-[#333333] focus:ring-2 focus:ring-[#2f4941]/30 focus:border-[#2f4941]"
                        />
                      </div>
                    )}
                    </div>
                    )}
                  </div>

                  {/* Avatar layout — single source of truth for HeyGen's avatar_layout (scale,
                      offset_y, avatar_style/matting). Sits next to Quality settings so all
                      pre-render tweaks live together; the preview mirrors these live. */}
                  {selectedAvatar && (
                    <div className="mb-3 rounded-lg border border-[#E5E7EB] bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-[13px] font-semibold text-[#1F2937]">Avatar layout</h3>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-[#6B7280]">
                            Scale, offset, and style — sent to HeyGen and mirrored in the preview.
                          </p>
                        </div>
                        <span className="rounded-full bg-[#F9FAFB] px-2 py-0.5 text-[10px] text-[#6B7280]">Optional</span>
                      </div>
                      <div className="mt-3 grid gap-4 md:grid-cols-2">
                        <label className="block">
                          <span className="flex items-center justify-between text-xs font-medium text-[#374151]">
                            Scale
                            <span className="font-mono text-[#6B7280]">{avatarScale.toFixed(1)}x</span>
                          </span>
                          <input
                            type="range"
                            min="0.5"
                            max="2"
                            step="0.1"
                            value={avatarScale}
                            onChange={(e) => setAvatarScale(Number(e.target.value))}
                            className="mt-2 w-full accent-[#B7793B]"
                          />
                        </label>
                        <label className="block">
                          <span className="flex items-center justify-between text-xs font-medium text-[#374151]">
                            Vertical offset
                            <span className="font-mono text-[#6B7280]">{avatarOffsetY.toFixed(2)}</span>
                          </span>
                          <input
                            type="range"
                            min="-0.3"
                            max="0.3"
                            step="0.05"
                            value={avatarOffsetY}
                            onChange={(e) => setAvatarOffsetY(Number(e.target.value))}
                            className="mt-2 w-full accent-[#B7793B]"
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="mb-4 flex-shrink-0 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-[#1F2937]">Scenes</h2>
                    {onReloadAvatars && (
                      <button
                        type="button"
                        onClick={() => void onReloadAvatars()}
                        disabled={loadingAvatars}
                        className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#6B7280] hover:bg-[#F3F4F6] disabled:opacity-40 transition-colors"
                        title="Reload avatars"
                      >
                        <svg className={`h-4 w-4 ${loadingAvatars ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="mb-4 flex-shrink-0 border-b border-[#E5E7EB]">
                    <div className="flex gap-5">
                      <button
                        type="button"
                        onClick={() => setAvatarSource('public')}
                        className={`pb-2.5 text-xs font-medium transition-colors ${
                          avatarSource === 'public'
                            ? 'border-b-2 border-[#B7793B] text-[#1F2937]'
                            : 'text-[#6B7280] hover:text-[#374151]'
                        }`}
                      >
                        Premade Avatars
                      </button>
                      <button
                        type="button"
                        onClick={() => setAvatarSource('custom')}
                        className={`pb-2.5 text-xs font-medium transition-colors ${
                          avatarSource === 'custom'
                            ? 'border-b-2 border-[#B7793B] text-[#1F2937]'
                            : 'text-[#6B7280] hover:text-[#374151]'
                        }`}
                      >
                        Custom Avatar
                      </button>
                    </div>
                  </div>
                    {avatarSource === 'public' ? (
                      (loadingAvatars || !avatarsFetched) ? (
                        <div>
                          <div className="mb-3 flex items-center justify-center gap-2 text-[12px] text-[#6B7280]">
                            <svg className="h-3.5 w-3.5 animate-spin text-[#B7793B]" fill="none" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                            </svg>
                            Loading avatars…
                          </div>
                          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            {Array.from({ length: 8 }).map((_, i) => (
                              <div key={i} className="animate-pulse rounded-xl border border-[#E5E7EB] bg-white">
                                <div className="aspect-[4/3] rounded-t-xl bg-[#F3F4F6]" />
                                <div className="px-3 py-2.5 space-y-1.5">
                                  <div className="h-3 w-16 rounded bg-[#E5E7EB]" />
                                  <div className="h-2.5 w-10 rounded bg-[#F3F4F6]" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : sortedPublicAvatars.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-12 text-center">
                          <svg className="h-8 w-8 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-[#374151]">Couldn’t load avatars</p>
                            <p className="mt-1 text-xs text-[#6B7280]">Please reload to see the avatars.</p>
                          </div>
                          {onReloadAvatars && (
                            <button
                              type="button"
                              onClick={() => void onReloadAvatars()}
                              disabled={loadingAvatars}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-medium text-[#374151] hover:bg-[#F3F4F6] disabled:opacity-50"
                            >
                              <svg className={`h-3.5 w-3.5 ${loadingAvatars ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Reload avatars
                            </button>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            {sortedPublicAvatars.map((avatar, index) => {
                              const isSelected = selectedAvatar?.avatar_id === avatar.avatar_id;
                              const persona = avatar.provider
                                ? avatar.provider.charAt(0).toUpperCase() + avatar.provider.slice(1)
                                : "Professional";
                              return (
                                <button
                                  key={`${avatar.avatar_id}-${index}`}
                                  ref={isSelected ? selectedAvatarRef : null}
                                  type="button"
                                  onClick={() => handlePickAvatar(avatar)}
                                  className={`group rounded-xl border bg-white text-left transition-all ${
                                    isSelected
                                      ? "border-[#B7793B] ring-2 ring-[#B7793B]/25"
                                      : "border-[#E5E7EB] hover:border-[#D1D5DB]"
                                  }`}
                                >
                                  <div className="relative aspect-[4/3] overflow-hidden rounded-t-xl bg-[#F3F4F6]">
                                    {!loadedThumbnailIds.has(avatar.avatar_id) && (
                                      <div className="absolute inset-0 z-[1] animate-pulse bg-[#E5E7EB]" aria-hidden />
                                    )}
                                    <img
                                      src={avatar.thumbnail_url}
                                      alt={avatar.name}
                                      loading="lazy"
                                      className={`h-full w-full object-cover transition-opacity duration-300 ${
                                        loadedThumbnailIds.has(avatar.avatar_id) ? 'opacity-100' : 'opacity-0'
                                      }`}
                                      onLoad={() => markThumbnailLoaded(avatar.avatar_id)}
                                      onError={() => markThumbnailFailed(avatar.avatar_id)}
                                    />
                                  </div>
                                  <div className="flex items-center justify-between px-3 py-2.5">
                                    <div>
                                      <p className="text-sm font-medium text-[#1F2937]">{avatar.name}</p>
                                      <p className="text-[11px] text-[#6B7280]">{persona}</p>
                                    </div>
                                    <span
                                      className={`h-4 w-4 rounded-full border ${
                                        isSelected
                                          ? "border-[#B7793B] bg-[#B7793B]"
                                          : "border-[#D1D5DB] bg-white group-hover:border-[#9CA3AF]"
                                      }`}
                                    />
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          <div className="mt-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
                            <p className="text-xs text-[#6B7280]">
                              These avatars are AI-generated presenters and may not match real persons. Select one to represent your video.
                            </p>
                          </div>
                        </>
                      )
                    ) : (
                      <div className="flex flex-col gap-4">
                        <p className="text-sm font-medium text-[#374151]">Upload or select a custom avatar</p>
                        <input
                          type="file"
                          ref={customAvatarFileInputRef}
                          accept="image/jpeg,image/png"
                          className="hidden"
                          onChange={handleCustomAvatarFileChange}
                          aria-hidden
                        />
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => customAvatarFileInputRef.current?.click()}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); customAvatarFileInputRef.current?.click(); } }}
                          className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#D1D5DB] bg-white transition-all hover:border-[#B7793B]/50 hover:bg-[#B7793B]/5"
                        >
                          {selectedCustomAvatarPreviewUrl ? (
                            <div className="relative w-full min-h-[120px] flex items-center justify-center p-2">
                              <img src={selectedCustomAvatarPreviewUrl} alt="" className="max-h-[140px] w-auto object-contain rounded-lg" />
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); clearCustomAvatarSelection(); }}
                                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                                aria-label="Remove image"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <>
                              <svg className="w-10 h-10 text-[#333333]/30 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" />
                              </svg>
                              <span className="text-sm font-medium text-[#4B5563]">Upload a photo</span>
                              <span className="mt-0.5 text-xs text-[#9CA3AF]">Click to select an image</span>
                            </>
                          )}
                        </div>
                        {customAvatarSuccessMessage && (
                          <div className="p-4 rounded-xl border border-green-600/40 bg-green-600/20">
                            <p className="text-sm text-[#2f4941]">{customAvatarSuccessMessage}</p>
                          </div>
                        )}
                        {customAvatarError && (
                          <div className="p-4 rounded-xl border border-red-500/40 bg-red-500/20">
                            <p className="text-sm text-red-700">{customAvatarError}</p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleGenerateCustomAvatar}
                          disabled={!selectedCustomAvatarImage || isUploadingCustomAvatar}
                          className="w-full py-2.5 px-4 rounded-lg bg-[#2f4941] text-white text-sm font-semibold hover:brightness-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100"
                        >
                          {isUploadingCustomAvatar ? 'Uploading…' : 'Generate custom avatar'}
                        </button>
                        <div className="mt-2 border-t border-[#E5E7EB] pt-4">
                          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Your custom avatars</h4>
                          {displayedCustomAvatars.length === 0 ? (
                            <p className="py-4 text-center text-sm text-[#6B7280]">
                              You do not have any custom avatars created. Upload a photo to get started.
                            </p>
                          ) : (
                            <div className="grid grid-cols-3 gap-3 lg:grid-cols-5">
                              {displayedCustomAvatars.map((avatar, index) => {
                                const isSelected =
                                  selectedAvatar?.avatar_type === 'talking_photo' &&
                                  selectedAvatar?.avatar_id === avatar.avatar_id;
                                return (
                                  <button
                                    key={`${avatar.avatar_id}-${index}`}
                                    type="button"
                                    onClick={() => handlePickAvatar(avatar)}
                                    className={`group rounded-xl border bg-white text-left transition-all max-w-[180px] ${
                                      isSelected
                                        ? "border-[#B7793B] ring-2 ring-[#B7793B]/25"
                                        : "border-[#E5E7EB] hover:border-[#D1D5DB]"
                                    }`}
                                  >
                                    <div className="aspect-square overflow-hidden rounded-t-xl bg-[#F3F4F6]">
                                      {avatar.thumbnail_url ? (
                                        <>
                                          <img
                                            src={avatar.thumbnail_url}
                                            alt={avatar.name}
                                            className="h-full w-full object-cover"
                                            onError={(e) => {
                                              const image = e.currentTarget;
                                              image.style.display = "none";
                                              const fallback = image.nextElementSibling as HTMLElement | null;
                                              if (fallback) fallback.style.display = "flex";
                                            }}
                                          />
                                          <div className="hidden h-full w-full items-center justify-center bg-[#F8FAFC] text-[#9CA3AF]">
                                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                          </div>
                                        </>
                                      ) : (
                                        <div className="flex h-full w-full items-center justify-center text-[#9CA3AF]">
                                          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center justify-between px-3 py-2.5">
                                      <div>
                                        <p className="text-sm font-medium text-[#1F2937]">{avatar.name}</p>
                                        <p className="text-[11px] text-[#6B7280]">Custom</p>
                                      </div>
                                      <span
                                        className={`h-4 w-4 rounded-full border ${
                                          isSelected
                                            ? "border-[#B7793B] bg-[#B7793B]"
                                            : "border-[#D1D5DB] bg-white group-hover:border-[#9CA3AF]"
                                        }`}
                                      />
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  <div className="sticky bottom-0 bg-white border-t border-gray-200 -mx-8 px-8 py-4 mt-6 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setActiveConfigStep(1)}
                      className="px-6 py-2.5 bg-[#F5F5F5] text-[#333333] rounded-lg text-sm font-semibold hover:bg-[#E5E5E5] transition-all border border-[#E5E5E5] flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveConfigStep(3)}
                      className="px-6 py-2.5 bg-[#2f4941] text-white rounded-lg text-sm font-semibold hover:brightness-95 transition-all flex items-center gap-2"
                    >
                      Next: Voice
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Voice */}
            {activeConfigStep === 3 && (
              <div className="flex-1 flex flex-col p-8 min-h-0 overflow-hidden">
                <div className="flex flex-col h-full min-h-0 pr-6">
                  <div className="mb-4 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-[#1F2937]">Customize</h2>
                      {onReloadVoices && (
                        <button
                          type="button"
                          onClick={() => void onReloadVoices()}
                          disabled={loadingVoices}
                          className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#6B7280] hover:bg-[#F3F4F6] disabled:opacity-40 transition-colors"
                          title="Reload voices"
                        >
                          <svg className={`h-4 w-4 ${loadingVoices ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-[#6B7280]">Choose the perfect voice to bring your script to life.</p>
                  </div>
                  <div className="mb-4 flex-shrink-0 border-b border-[#E5E7EB]">
                    <div className="flex gap-5">
                      <button
                        type="button"
                        onClick={() => setVoiceTab('ai')}
                        className={`pb-2.5 text-xs font-medium ${voiceTab === 'ai' ? 'border-b-2 border-[#B7793B] text-[#1F2937]' : 'text-[#6B7280] hover:text-[#1F2937]'}`}
                      >
                        AI Voices
                      </button>
                      <button
                        type="button"
                        onClick={() => setVoiceTab('custom')}
                        className={`pb-2.5 text-xs font-medium ${voiceTab === 'custom' ? 'border-b-2 border-[#B7793B] text-[#1F2937]' : 'text-[#6B7280] hover:text-[#1F2937]'}`}
                      >
                        Custom Voice
                      </button>
                    </div>
                  </div>

                  {/* Custom Voice: clone-from-sample upload card */}
                  {voiceTab === 'custom' && (
                    <div className="mb-4 flex-shrink-0 rounded-xl border border-[#E5E7EB] bg-white p-4">
                      <p className="text-xs font-semibold text-[#1F2937]">Clone your voice</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-[#6B7280]">
                        Upload ~30 seconds to 2 minutes of clear speech (no music or background noise).
                        Your cloned voice can then read any script.
                      </p>
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <input
                          type="text"
                          value={customVoiceName}
                          onChange={(e) => setCustomVoiceName(e.target.value.slice(0, 50))}
                          placeholder="Voice name (e.g. my voice)"
                          disabled={isUploadingVoice}
                          className="flex-1 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs text-[#374151] placeholder:text-[#9CA3AF] disabled:opacity-60"
                        />
                        <button
                          type="button"
                          onClick={() => customVoiceFileInputRef.current?.click()}
                          disabled={isUploadingVoice}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#2f4941] px-4 py-2 text-xs font-semibold text-white hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUploadingVoice ? (
                            <>
                              <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                              Cloning…
                            </>
                          ) : (
                            'Upload audio sample'
                          )}
                        </button>
                        <input
                          ref={customVoiceFileInputRef}
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={handleCustomVoiceFileChange}
                        />
                      </div>
                      {customVoiceError && (
                        <p className="mt-2 text-[11px] text-red-600" role="alert">{customVoiceError}</p>
                      )}
                    </div>
                  )}

                  <div className={`mb-4 flex-shrink-0 grid grid-cols-1 gap-2 ${voiceTab === 'custom' ? 'md:grid-cols-1 hidden' : 'md:grid-cols-4'}`}>
                    <select
                      value={voiceLanguageFilter}
                      onChange={(e) => setVoiceLanguageFilter(e.target.value)}
                      className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs text-[#374151]"
                    >
                      <option value="all">All Languages</option>
                      <option value="english">English</option>
                    </select>
                    <select
                      value={voiceGenderFilter}
                      onChange={(e) => setVoiceGenderFilter(e.target.value)}
                      className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs text-[#374151]"
                    >
                      <option value="all">All Genders</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                    </select>
                    <select
                      value={voiceAccentFilter}
                      onChange={(e) => setVoiceAccentFilter(e.target.value)}
                      className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs text-[#374151]"
                    >
                      <option value="all">All Accents</option>
                      <option value="neutral">Neutral</option>
                      <option value="british">British</option>
                      <option value="australian">Australian</option>
                      <option value="indian">Indian</option>
                    </select>
                    <input
                      type="text"
                      value={voiceSearchQuery}
                      onChange={(e) => setVoiceSearchQuery(e.target.value)}
                      placeholder="Search voices..."
                      className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs text-[#374151] placeholder:text-[#9CA3AF]"
                    />
                  </div>

                  {/* Same in-DOM <audio> path as the legacy Voice Preview "Play" control (avoids new Audio() quirks). */}
                  <audio
                    ref={voiceSampleListAudioRef}
                    preload="none"
                    playsInline
                    className="hidden"
                    aria-hidden={true}
                  />

                  <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-[#E5E7EB] bg-white">
                    {loadingVoices ? (
                      <div className="divide-y divide-[#F1F5F9]">
                        {Array.from({ length: 7 }).map((_, i) => (
                          <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                            <div className="h-4 w-4 flex-shrink-0 rounded-full bg-[#E5E7EB]" />
                            <div className="h-7 w-7 flex-shrink-0 rounded-full bg-[#E5E7EB]" />
                            <div className="flex-1 space-y-1.5">
                              <div className="h-3 w-28 rounded bg-[#E5E7EB]" />
                              <div className="h-2.5 w-16 rounded bg-[#F3F4F6]" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : displayedVoices.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-1 py-12 text-sm text-[#6B7280]">
                        {voiceTab === 'custom' ? (
                          <>
                            <span>No custom voices yet.</span>
                            <span className="text-xs text-[#9CA3AF]">Upload an audio sample above to clone your voice.</span>
                          </>
                        ) : (
                          'No voices available'
                        )}
                      </div>
                    ) : (
                      <div className="divide-y divide-[#F1F5F9]">
                        {displayedVoices.map((voice, index) => {
                          const isSelected = selectedVoice?.voice_id === voice.voice_id;
                          const isSamplePlaying = playingVoiceSampleId === voice.voice_id;
                          const accent = getVoiceAccent(voice);
                          return (
                            <div
                              key={`${voice.voice_id}-${index}`}
                              tabIndex={0}
                              onClick={() => onSelectVoice(voice)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onSelectVoice(voice);
                                }
                              }}
                              className={`flex w-full cursor-pointer select-none items-center gap-3 px-4 py-3 text-left transition-colors ${
                                isSelected ? "bg-[#FFF8F1]" : "hover:bg-[#F8FAFC]"
                              }`}
                            >
                              <span
                                className={`h-4 w-4 flex-shrink-0 rounded-full border ${
                                  isSelected ? "border-[#B7793B] bg-[#B7793B]" : "border-[#D1D5DB] bg-white"
                                }`}
                              />
                              {voice.sample_url ? (
                                <button
                                  type="button"
                                  data-ugc-voice-sample
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    void handlePlayVoiceListSample(voice);
                                  }}
                                  className={`relative z-20 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border transition-colors ${
                                    isSamplePlaying
                                      ? "border-[#B7793B] bg-[#B7793B] text-white"
                                      : "border-[#E5E7EB] text-[#6B7280] hover:border-[#B7793B]/50 hover:text-[#B7793B]"
                                  }`}
                                  title={isSamplePlaying ? "Stop sample" : "Play sample"}
                                >
                                  {isSamplePlaying ? (
                                    <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                                      <rect x="6" y="4" width="4" height="16" rx="1" />
                                      <rect x="14" y="4" width="4" height="16" rx="1" />
                                    </svg>
                                  ) : (
                                    <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  )}
                                </button>
                              ) : (
                                <span
                                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-[#E5E7EB] text-[#6B7280] opacity-50"
                                  title="No sample"
                                >
                                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                </span>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-[#1F2937]">{voice.name}</p>
                                  {isSelected && (
                                    <span className="rounded-full bg-[#FDEBD7] px-2 py-0.5 text-[10px] font-medium text-[#B7793B]">
                                      Recommended
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-[#6B7280]">
                                  {voice.gender || "Unknown"} • {accent} • {getVoiceTone(voice)}
                                </p>
                              </div>
                              <span className="text-[11px] text-[#6B7280]">{accent}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {selectedVoice && (
                    <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-white px-4 py-3">
                      <p className="text-xs font-medium text-[#374151]">Voice Preview</p>
                      <div className="mt-2">
                        <p className="text-sm font-medium text-[#1F2937]">{selectedVoice.name}</p>
                        <p className="text-[11px] text-[#6B7280]">{getVoiceTone(selectedVoice)}</p>
                        {selectedVoice.sample_url && playingVoiceSampleId === selectedVoice.voice_id && (
                          <p className="mt-1.5 text-[11px] text-[#B7793B]">Playing sample…</p>
                        )}
                        {!selectedVoice.sample_url && (
                          <p className="mt-1.5 text-[11px] text-[#9CA3AF]">No audio sample for this voice.</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex-shrink-0 border-t border-gray-200 mt-4 pt-4 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setActiveConfigStep(2)}
                      className="px-6 py-2.5 bg-[#F5F5F5] text-[#333333] rounded-lg text-sm font-semibold hover:bg-[#E5E5E5] transition-all border border-[#E5E5E5] flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>
                    {UGC_CUSTOM_IMAGES_ENABLED ? (
                      generatedVideoUrl ? (
                        <button
                          type="button"
                          onClick={() => setActiveConfigStep(4)}
                          className="px-6 py-2.5 bg-[#2f4941] text-white rounded-lg text-sm font-semibold hover:brightness-95 transition-all flex items-center gap-2"
                        >
                          Next: Edit Video
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleGenerateVideo}
                          disabled={!hasScript || !selectedAvatar || !selectedVoice || isGeneratingVideo || isOverLimit}
                          className="px-6 py-2.5 bg-[#2f4941] text-white rounded-lg text-sm font-semibold hover:brightness-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isGeneratingVideo ? (
                            <>
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Processing video...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Process Video
                            </>
                          )}
                        </button>
                      )
                    ) : (
                      generatedVideoUrl ? (
                        <button
                          type="button"
                          onClick={() => setActiveConfigStep(4)}
                          className="px-6 py-2.5 bg-[#2f4941] text-white rounded-lg text-sm font-semibold hover:brightness-95 transition-all flex items-center gap-2"
                        >
                          Next: Post Details
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleGenerateVideo}
                          disabled={!hasScript || !selectedAvatar || !selectedVoice || isGeneratingVideo || isOverLimit}
                          className="px-6 py-2.5 bg-[#2f4941] text-white rounded-lg text-sm font-semibold hover:brightness-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isGeneratingVideo ? (
                            <>
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Processing video...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Process Video
                            </>
                          )}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: Edit Video — Always show full editor. Auto-population runs in background. */}
            {UGC_CUSTOM_IMAGES_ENABLED && activeConfigStep === 4 && (
              <div className="relative flex flex-1 flex-col min-h-0">
                <UGCVideoEditor
                  generatedVideoUrl={generatedVideoUrl}
                  baseVideoDurationSeconds={localBaseVideoDuration ?? baseVideoDurationSeconds}
                  timelineSegments={timelineSegments}
                  onReorderSegments={onReorderSegments}
                  onUpdateSegment={onUpdateSegment}
                  onRemoveSegment={onRemoveSegment}
                  textOverlays={textOverlays}
                  onAddTextOverlay={addTextOverlay}
                  onUpdateTextOverlay={updateTextOverlay}
                  onRemoveTextOverlay={removeTextOverlay}
                  onApplyTextTemplate={applyTextTemplate}
                  onApplyEdits={handleApplyCustomImages}
                  isApplyingEdits={assembly.isAssembling}
                  customerId={customerId}
                  idToken={idToken}
                  agentTools={agentTools}
                  scriptContent={transcriptForVideo || scriptContent}
                  selectedMediaIds={selectedMediaIds}
                  thumbnailById={segmentThumbnailsMap}
                  onToggleMedia={onToggleMedia}
                  onClearAllMedia={() => onClearSelectedMedia?.()}
                  onMediaLoaded={(items) => ingestMediaItems(items as MediaItem[])}
                  onVideoMediaImported={(mediaId, videoUrl) =>
                    setSegmentVideoUrlsMap((prev) => ({ ...prev, [mediaId]: videoUrl }))
                  }
                  segmentVideoUrls={segmentVideoUrlsMap}
                  enableLibraryDrag={Boolean(onPlaceMediaOnTimeline)}
                  captionsEnabled={captionsEnabled}
                  onCaptionsEnabledChange={handleCaptionsEnabledChange}
                  isAnalyzingCaptions={isAnalyzingCaptions}
                  captionNotice={captionNotice}
                  musicPresetId={musicPresetId}
                  onMusicPresetChange={setMusicPresetId}
                  customMusicUrl={customMusicUrl}
                  onCustomMusicUrlChange={setCustomMusicUrl}
                  musicVolumePercent={musicMixVolumePercent}
                  onMusicVolumeChange={setMusicMixVolumePercent}
                  avatarZoom={avatarScale}
                  onAvatarZoomChange={(v) => setAvatarScale(clampAvatarScale(v))}
                  avatarOffsetY={avatarOffsetY}
                  avatarStyle={avatarStyle}
                  isTalkingPhotoAvatar={isTalkingPhotoAvatar}
                  avatarMatting={avatarMatting}
                  videoFormat={selectedVideoFormat}
                  hideHeader
                />
              </div>
            )}

            {/* STEP 5 (or STEP 4 when custom images disabled): Post Details - platform, caption, save */}
            {((UGC_CUSTOM_IMAGES_ENABLED && activeConfigStep === 5) || (!UGC_CUSTOM_IMAGES_ENABLED && activeConfigStep === 4)) && (
              <div className="flex-1 flex flex-col p-6 sm:p-8 min-h-0 overflow-hidden">
                <div className="flex flex-col h-full min-h-0 overflow-y-auto space-y-6 pr-2 sm:pr-6">
                  <div className="rounded-2xl border border-[#E5E5E5] bg-white shadow-sm p-5 sm:p-6 space-y-6">
                    <div className="flex items-start gap-3">
                      <div
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] text-[#2f4941]"
                        aria-hidden
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-[#1F2937]">
                          Finalize
                        </h2>
                        <p className="text-sm text-[#6B7280] mt-0.5">
                          Select platforms, write your caption, and save to Review Posts.
                        </p>
                      </div>
                    </div>

                    {/* Target Platform */}
                    <section>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-1">Target Platform</h3>
                      <p className="text-sm text-[#1F2937] mb-1">Choose one or more platforms to customize your post.</p>
                      <p className="text-xs text-[#6B7280] mb-3">
                        Pick where you want captions tailored. Saving sends posts to Review Posts — you can publish later when each platform is connected.
                      </p>
                      {/* 2 columns: gives each card enough width for the full platform label
                          ("Instagram Reels", "YouTube Shorts") on one line — md:grid-cols-4
                          collapsed the labels letter-per-line inside this narrow right panel. */}
                      <div className="grid grid-cols-2 gap-3">
                        {REEL_PLATFORMS.filter(
                          (p) => p !== Platform.TIKTOK_REELS || isTikTokPostingAvailable(connectedProfiles)
                        ).map((platform) => {
                          const isSelected = selectedPlatforms.includes(platform);
                          return (
                            <button
                              key={platform}
                              type="button"
                              onClick={() => togglePlatform(platform)}
                              className={`relative flex min-h-[4.5rem] w-full flex-col items-stretch justify-center rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
                                isSelected
                                  ? 'border-[#2f4941] bg-[#fffdf9] text-[#1F2937] shadow-sm'
                                  : 'border-[#E5E5E5] bg-white text-[#6B7280] hover:border-[#d1d5db]'
                              }`}
                            >
                              <span
                                className={`absolute right-2 top-2 z-[1] flex h-5 w-5 items-center justify-center rounded ${
                                  isSelected ? 'text-[#2f4941]' : 'border border-[#D1D5DB] bg-white'
                                }`}
                                aria-hidden
                              >
                                {isSelected ? (
                                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : null}
                              </span>
                              <span className="flex min-w-0 flex-col gap-2 pr-10 sm:flex-row sm:items-center sm:gap-2.5">
                                <span
                                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border ${
                                    isSelected
                                      ? 'border-[#2f4941]/50 bg-white shadow-sm'
                                      : 'border-[#E5E5E5] bg-[#FAFAFA]'
                                  }`}
                                >
                                  <ReelPlatformIcon platform={platform} />
                                </span>
                                <span
                                  className={`min-w-0 text-xs font-semibold leading-snug text-balance break-words ${
                                    isSelected ? 'text-[#1F2937]' : 'text-[#4B5563]'
                                  }`}
                                >
                                  {getPlatformLabel(platform)}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {selectedPlatforms.includes(Platform.LINKEDIN_REELS) && (() => {
                        const { personal, business } = getConnectedLinkedInTargets(connectedProfiles);
                        if (!personal && !business) return null;
                        const effective: "personal" | "business" = linkedinTarget ?? (business ? "business" : "personal");
                        return (
                          <div className="mt-3">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-1.5">
                              LinkedIn: Post to
                            </h4>
                            <div className="flex gap-2 rounded-xl bg-[#F3F4F6] p-1">
                              {(["personal", "business"] as const).map((t) => {
                                const connected = t === "personal" ? personal : business;
                                return (
                                  <button
                                    key={t}
                                    type="button"
                                    disabled={!connected}
                                    title={connected ? undefined : "Connect this LinkedIn account in Connections to use it."}
                                    onClick={() => setLinkedinTarget(t)}
                                    className={`flex-1 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                                      effective === t
                                        ? 'bg-[#2f4941] text-white shadow-sm'
                                        : 'bg-white text-[#6B7280] hover:bg-[#FAFAFA]'
                                    }`}
                                  >
                                    {t === "personal" ? "Personal" : "Business Page"}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </section>

                    {/* Video Thumbnail */}
                    <section>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2">Video Thumbnail</h3>
                      <p className="text-xs text-[#6B7280] mb-2">
                        Optional cover image. Pick a frame from your video or upload an image.
                      </p>
                      <input
                        type="file"
                        ref={thumbnailFileInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={handleThumbnailFileChange}
                        disabled={isUploadingThumbnail}
                        aria-hidden
                      />
                      <button
                        type="button"
                        onClick={handleThumbnailUploadClick}
                        disabled={isUploadingThumbnail}
                        className="mb-3 text-xs font-semibold text-[#2f4941] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isUploadingThumbnail ? 'Uploading…' : 'Upload cover image'}
                      </button>
                      <div className="flex flex-wrap gap-3 max-w-full">
                        <button
                          type="button"
                          onClick={() => setSelectedThumbnailSource('video')}
                          className={`aspect-square w-20 h-20 rounded-xl overflow-hidden relative flex items-center justify-center bg-[#333333] border-2 transition-colors flex-shrink-0 ${
                            selectedThumbnailSource === 'video' ? 'border-[#2f4941] ring-1 ring-[#2f4941]/20' : 'border-[#E5E5E5] hover:border-[#333333]/30'
                          }`}
                        >
                          {generatedVideoUrl ? (
                            <video
                              key={generatedVideoUrl}
                              src={generatedVideoUrl}
                              className="max-h-full max-w-full object-contain"
                              muted
                              playsInline
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="w-10 h-10 rounded-full bg-[#2f4941]/30 flex items-center justify-center">
                                <svg className="w-5 h-5 text-[#2f4941]" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                            </div>
                          )}
                          {selectedThumbnailSource === 'video' && (
                            <div className="absolute top-1 right-1 w-4 h-4 bg-[#2f4941] rounded-full flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </button>
                        {customThumbnailUrls.map((url, idx) => (
                          <button
                            key={url}
                            type="button"
                            onClick={() => {
                              setSelectedThumbnailSource('custom');
                              setSelectedCustomThumbnailIndex(idx);
                            }}
                            className={`aspect-square w-20 h-20 rounded-xl overflow-hidden relative flex items-center justify-center bg-[#333333] border-2 transition-colors flex-shrink-0 ${
                              selectedThumbnailSource === 'custom' && selectedCustomThumbnailIndex === idx
                                ? 'border-[#2f4941] ring-1 ring-[#2f4941]/20'
                                : 'border-[#E5E5E5] hover:border-[#333333]/30'
                            }`}
                          >
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            {selectedThumbnailSource === 'custom' && selectedCustomThumbnailIndex === idx && (
                              <div className="absolute top-1 right-1 w-4 h-4 bg-[#2f4941] rounded-full flex items-center justify-center">
                                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                      {thumbnailUploadError && (
                        <p className="mt-2 text-xs text-red-600" role="alert">
                          {UPLOAD_ERROR_MESSAGE}
                        </p>
                      )}
                    </section>

                    {/* Caption */}
                    <section>
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">Caption &amp; Tags</h3>
                          <p className="text-sm text-[#1F2937] mt-1 max-w-xl">
                            We auto-write a caption from your script once the video finishes rendering — edit it however you like (hashtags and line breaks are fine).
                          </p>
                        </div>
                        {isGeneratingCaption && (
                          <span className="mt-0.5 inline-flex flex-shrink-0 items-center gap-1.5 text-xs font-medium text-[#2f4941]">
                            <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Writing caption…
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <textarea
                          value={generatedCaption}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v.length <= CAPTION_CHAR_LIMIT) setGeneratedCaption(v);
                          }}
                          disabled={isGeneratingCaption}
                          placeholder={isGeneratingCaption ? 'Writing your caption…' : 'Write your caption here…'}
                          rows={8}
                          className="w-full min-h-[10rem] resize-y rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 pr-20 pb-7 text-sm leading-relaxed text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#2f4941]/50 focus:outline-none focus:ring-2 focus:ring-[#2f4941]/15 disabled:cursor-not-allowed disabled:bg-[#F9FAFB]"
                        />
                        <div className="pointer-events-none absolute bottom-2 right-3 text-xs tabular-nums text-[#9CA3AF]">
                          {generatedCaption.length} / {CAPTION_CHAR_LIMIT}
                        </div>
                      </div>
                    </section>

                    {/* Tags */}
                    <section>
                      <h3 className="text-xs font-semibold text-[#1F2937] mb-2">Add tags (optional)</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        {tags.map((tag, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-[#F9FAFB] px-2.5 py-1 text-xs font-medium text-[#374151]"
                          >
                            {tag.startsWith('#') ? tag : `#${tag}`}
                            <button
                              type="button"
                              onClick={() => setTags((t) => t.filter((_, idx) => idx !== i))}
                              className="rounded-full p-0.5 text-[#9CA3AF] hover:bg-[#E5E5E5] hover:text-[#1F2937]"
                              aria-label={`Remove ${tag}`}
                            >
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        ))}
                        <div className="inline-flex max-w-full flex-wrap items-center gap-1.5">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const t = newTag.trim().replace(/^#/, '');
                                if (t) {
                                  setTags((prev) => [...prev, t]);
                                  setNewTag('');
                                }
                              }
                            }}
                            placeholder="Add tag..."
                            className="min-w-[6rem] flex-1 rounded-lg border border-[#E5E5E5] bg-white px-2.5 py-1.5 text-xs text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#2f4941]/40 focus:outline-none focus:ring-1 focus:ring-[#2f4941]/20 sm:max-w-[12rem]"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const t = newTag.trim().replace(/^#/, '');
                              if (t) {
                                setTags((prev) => [...prev, t]);
                                setNewTag('');
                              }
                            }}
                            className="rounded-lg border border-[#E5E5E5] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#6B7280] transition-colors hover:border-[#d1d5db] hover:text-[#1F2937]"
                          >
                            + Add
                          </button>
                        </div>
                      </div>
                    </section>

                    {!generatedVideoUrl && (
                      <div
                        className="flex gap-2 rounded-lg border border-[#2f4941]/50 bg-[#f0f4f2] px-3 py-2.5 text-xs text-[#6B7280]"
                        role="status"
                      >
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-[#2f4941]/40 text-[#2f4941]" aria-hidden>
                          <span className="text-[10px] font-serif font-bold">i</span>
                        </span>
                        <p className="leading-relaxed">
                          Create your video first. Then select platforms above and save to Review Posts.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-2 flex flex-col gap-3 border-t border-[#E5E5E5] pt-5 sm:mt-4 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={() => setActiveConfigStep(UGC_CUSTOM_IMAGES_ENABLED ? 4 : 3)}
                      className="order-2 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-[#6B7280] transition-colors hover:text-[#1F2937] sm:order-1"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                      {UGC_CUSTOM_IMAGES_ENABLED ? 'Back to Edit Video' : 'Back to Voice'}
                    </button>
                    <div className="order-1 flex w-full flex-col items-stretch gap-2 sm:order-2 sm:w-auto sm:items-end">
                      {onSaveToPlatform && (
                        <button
                          type="button"
                          onClick={handleSaveToReviewPosts}
                          // Block save while Creatomate is still cropping the avatar into the
                          // chosen format — otherwise a fast click could ship the raw 16:9 clip.
                          disabled={!generatedVideoUrl || selectedPlatforms.length === 0 || isSavingVideos || assembly.isAssembling}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#2f4941] py-3 px-5 text-sm font-bold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                        >
                          {isSavingVideos ? (
                            <>
                              <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Saving…
                            </>
                          ) : (
                            <>
                              Save to Review Posts
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </>
                          )}
                        </button>
                      )}
                      {onResetVideo && generatedVideoUrl && (
                        <button
                          type="button"
                          onClick={() => onResetVideo?.()}
                          className="text-center text-xs font-medium text-[#9CA3AF] underline-offset-2 hover:text-[#6B7280] hover:underline"
                        >
                          Regenerate video
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default UGCVideoCreationModal;
